/**
 * Bilingual UI strings.
 *
 * English is the default UI locale — it is the candidate's language. Spanish
 * is a full peer, not a fallback: much of the field team and District 8 itself
 * work in it, Spanish-speaking staff switch with EN/ES, and both dictionaries
 * carry every key, enforced by the type system.
 *
 * Campaign-finance and election-law terms keep their English form in the
 * Spanish strings where that is the term of art staff will see on a filing
 * (for example "Texas Ethics Commission"). Translating those would make the
 * app disagree with the documents it is describing.
 */

import { briefingEn, briefingEs } from "../features/briefing/strings";
import { callTimeEn, callTimeEs } from "../features/calltime/strings";
import { mediaScoutEn, mediaScoutEs } from "../features/mediascout/strings";
import { practiceEn, practiceEs } from "../features/practice/strings";

export type Locale = "es" | "en";

const baseEn = {
  "app.name": "Canales CampaignOS",
  "app.tagline": "Chris Canales · El Paso City Council, District 8 · 2026",

  "nav.dashboard": "Campaign overview",
  "nav.advisor": "Ask the campaign",
  "nav.evidence": "Evidence",
  "nav.work": "Tasks",
  "nav.finance": "Finance",
  "nav.filings": "Campaign finance filings",
  "nav.contacts": "Contacts",
  "nav.issues": "Open questions",
  "nav.decisions": "Decisions",
  "nav.risks": "Risks",
  "nav.vendors": "Vendors",
  "nav.claimsReview": "Approve facts",
  "nav.report": "Weekly report",
  "nav.admin": "Administration",
  "nav.memory": "What we know",
  "nav.warRoom": "Research tools",
  "nav.watchdog": "What people are saying",
  "nav.field": "Field map",
  "nav.calendar": "Calendar",
  "nav.board": "Team chat",
  "nav.projects": "Plan",
  "nav.settings": "Settings",
  "nav.guide": "Guide",
  "nav.console": "Maintenance",
  "nav.hermes": "Drafting desk",
  "nav.signOut": "Sign out",
  "nav.menu": "Open the menu",
  "console.title": "Maintenance",
  "console.subtitle":
    "Authenticated maintenance surface. Every command goes through permissions and lands in the audit log.",
  "console.ready": "Console ready. Type 'help'.",
  "console.command": "Maintenance command",
  "console.run": "Run",
  "console.log": "Command output",
  "console.destructiveConfirm":
    "Run {command}? This deletes data and cannot be undone.",
  "nav.more": "More",
  "tab.today": "Today",
  "tab.overview": "Overview",
  "tab.calls": "Calls",
  "tab.practice": "Practice",
  "tab.media": "Media",
  "tab.tasks": "Tasks",
  "tab.calendar": "Calendar",
  "tab.chat": "Chat",
  "nav.primary": "Main sections",
  "nav.language": "Language",
  "nav.today": "My campaign today",
  "nav.callTime": "Call time",
  "nav.practice": "Debate practice",
  "nav.mediaScout": "Media scout",
  "common.comingSoon": "This tool is being set up.",

  "auth.title": "Sign in",
  "auth.platformOverview": "Platform overview",
  "auth.resetPassword": "Set or reset password",
  "auth.demoKicker": "Demo · read only",
  "auth.demoTitle": "Explore the guided demo",
  "auth.demoBody": "Fictional, isolated data. It never touches the live campaign.",
  "auth.demoLaunch": "Launch demo",
  "auth.demoOpening": "Opening…",
  "auth.resetSent": "If the email belongs to an account, a secure password-setting link will arrive.",
  "auth.resetFailed":
    "The link could not be sent — this is a problem with the site, not your account. Tell whoever set this up.",
  "auth.subtitle": "Internal campaign system. Authorized staff only.",
  "auth.email": "Email",
  "auth.password": "Password",
  "auth.submit": "Sign in",
  "auth.working": "Signing in…",
  "auth.failed": "Sign-in failed. Check your email and password.",
  "auth.noAccount":
    "No account record was found for you. Ask an administrator to provision access.",
  "auth.suspended": "This account is suspended. Contact an administrator.",
  "auth.wrongCampaign":
    "This account belongs to a different campaign. Canales CampaignOS is exclusively for Chris Canales's El Paso City Council District 8 campaign.",

  "dash.greeting": "Good to see you, {name}",
  "dash.role": "Role",
  "dash.strategistTitle": "Strategy command center",
  "dash.evidenceBase": "Evidence base",
  "dash.approvedEvidence": "Approved and citable",
  "dash.pendingReview": "Awaiting review",
  "dash.pendingSources": "Registered, not yet inspected",
  "dash.recentAnswers": "Recent advisor answers",
  "dash.openFlags": "Open compliance flags",
  "dash.overdueTasks": "Overdue work",
  "dash.upcomingFilings": "Upcoming filings",
  "dash.filingLate": "Late",
  "dash.filingDueIn": "Due in {n}d",
  "dash.noAnswers": "No advisor answers yet.",
  "dash.researchGaps": "Outstanding research",
  "dash.researchGapsHelp":
    "Sources the campaign has registered but not yet inspected. The advisor will not cite these.",
  "dash.askAdvisor": "Ask the campaign",
  "dash.action.title": "Start here",
  "dash.action.reviewFlags": "Review {n} flag(s)",
  "dash.action.openMap": "Open the field map",
  "dash.action.openFinance": "Open finance",
  "dash.action.myTasks": "See my tasks",
  "dash.uploadEvidence": "Add evidence",
  "dash.issueCoverage": "Evidence by issue cluster",
  "dash.noCoverage": "No approved evidence is filed against any issue cluster yet.",
  "dash.commandCenter": "Campaign overview",
  "dash.daysToElection": "Days to election",
  "dash.daysToFiling": "Days to filing close",
  "dash.electionDay": "Election · November 3, 2026",
  "dash.daysToRunoff": "Days to the runoff",
  "dash.runoffDay": "Runoff · December 12, 2026",
  "dash.runoffOver": "The runoff has passed.",
  "dash.filingClose": "Filing closes · August 17, 2026",
  "dash.filingClosed": "Filing closed",
  "dash.electionPassed": "Election day",
  "dash.days": "days",
  "dash.knowledge": "Knowledge items",
  "dash.mentions7d": "Mentions (7d)",
  "dash.watchdogPulse": "What people are saying",
  "dash.sentiment": "Sentiment of tracked mentions",
  "dash.climbing": "Stories picking up coverage",
  "dash.breakoutAlert": "Breaking into news",
  "dash.nothingClimbing": "No narratives have broken into news coverage.",
  "dash.objectives": "Current objectives",
  "dash.noObjectives": "No objectives set for this week yet.",
  "dash.setObjectives": "Set this week's objectives",
  "dash.openWatchdog": "See what people are saying",
  "dash.runSweep": "Check for mentions to fill this in.",

  "advisor.title": "Ask the campaign",
  "advisor.subtitle":
    "Answers are built only from approved evidence in this system. Every factual claim carries a citation, and claims that fail verification are removed before you see them.",
  "advisor.convertToTask": "Convert to task →",
  "advisor.taskAdded": "Added to Work",
  "advisor.question": "Question",
  "advisor.questionPlaceholder":
    "What does our evidence establish about downtown public safety in District 8?",
  "advisor.purpose": "Purpose",
  "advisor.purpose.research": "Research",
  "advisor.purpose.briefing": "Briefing",
  "advisor.purpose.operations": "Operations",
  "advisor.purpose.communications": "Communications",
  "advisor.purpose.field": "Field",
  "advisor.purpose.compliance_support": "Compliance support",
  "advisor.issueCluster": "Topic",
  "advisor.allClusters": "All topics",
  "advisor.ask": "Ask",
  "advisor.thinking": "Retrieving evidence and reasoning…",
  "advisor.summary": "Summary",
  "advisor.claims": "Claims",
  "advisor.sources": "Sources used",
  "advisor.modelAuthoredNote":
    "Written by the model, not verified against evidence. Treat as a research prompt, not a finding.",
  "advisor.gaps": "Evidence gaps",
  "advisor.required": "Sources that would close these gaps",
  "advisor.noAnswerTitle": "Not answerable from current evidence",
  "advisor.quarantined": "Withheld — the source didn't back it up",
  "advisor.quarantinedHelp":
    "These claims failed mechanical citation checks and were withheld. They are shown for transparency, not for use.",
  "advisor.injection": "Prompt-injection attempt detected in source material",
  "advisor.verified": "Verified",
  "advisor.verifiedHelp": "Every claim resolved to supplied evidence.",
  "advisor.partial": "Partially verified",
  "advisor.partialHelp": "Some claims were withheld. See below.",
  "advisor.failed": "Failed verification",
  "advisor.outOfScope": "Out of scope",
  "advisor.noEvidence": "No evidence",
  "advisor.refused": "Declined",
  "advisor.provider": "Model",
  "advisor.excluded": "Evidence considered and excluded",
  "advisor.markReviewed": "Mark reviewed",
  "advisor.accepted": "Accept",
  "advisor.rejected": "Reject",
  "advisor.needsWork": "Needs work",
  "advisor.reviewSaved": "Review recorded.",
  "advisor.history": "Recent questions",

  "claim.fact": "Fact",
  "claim.allegation": "Allegation",
  "claim.response": "Response",
  "claim.inference": "Inference",
  "claim.recommendation": "Recommendation",
  "claim.confidence": "Confidence",
  "claim.high": "High",
  "claim.medium": "Medium",
  "claim.low": "Low",

  "reliability.primary": "Primary",
  "reliability.official_campaign": "Official campaign",
  "reliability.secondary": "Secondary",
  "reliability.anecdotal": "Anecdotal",
  "reliability.pending": "Not reviewed yet",

  "aura.outcome.verified": "Verified",
  "aura.outcome.verified_with_quarantine": "Partially verified",
  "aura.outcome.failed_verification": "Failed verification",
  "aura.outcome.refused_on_retry": "Declined on retry",
  "aura.outcome.no_evidence": "No evidence",
  "aura.outcome.out_of_scope": "Out of scope",
  "aura.outcome.refused": "Declined",

  "compliance.rule.RELATED_PARTY": "Related-party payment",
  "compliance.rule.POSSIBLE_PERSONAL_USE": "Possible personal use",
  "compliance.rule.MEAL_WITHOUT_ATTENDEES": "Meal without attendees listed",
  "compliance.rule.FOREIGN_LOCATION": "Foreign location",
  "compliance.rule.MISSING_RECEIPT": "Missing receipt",
  "compliance.rule.POSSIBLE_DUPLICATE": "Possible duplicate",
  "compliance.rule.THIN_BUSINESS_PURPOSE": "Thin business purpose",

  "cluster.government_finance": "Government finance",
  "cluster.public_safety": "Public safety",
  "cluster.transportation": "Transportation",
  "cluster.housing": "Housing",
  "cluster.built_environment": "Built environment",
  "cluster.economic_development": "Economic development",
  "cluster.preservation": "Preservation",
  "cluster.neighborhood_services": "Neighborhood services",

  "mention.sourceType.news": "News",
  "mention.sourceType.blog": "Blog",
  "mention.sourceType.social": "Social",
  "mention.sourceType.official": "Official",
  "mention.sourceType.forum": "Forum",
  "mention.sourceType.other": "Other",

  "evidence.title": "Evidence",
  "evidence.subtitle":
    "Nothing here reaches the advisor until a person approves an excerpt for it.",
  "evidence.add": "Add evidence",
  "evidence.docTitle": "Title",
  "evidence.file": "File",
  "evidence.sourceType": "Source type",
  "evidence.publisher": "Publisher",
  "evidence.publishedDate": "Published date",
  "evidence.sourceUrl": "Source URL",
  "evidence.geography": "Geography",
  "evidence.clusters": "Topics",
  "evidence.rights": "Rights and permission note",
  "evidence.upload": "Upload",
  "evidence.uploading": "Uploading… {percent}%",
  "evidence.extracting": "Extracting text. This can take a few minutes for audio and video.",
  "evidence.status": "Status",
  "evidence.status.awaiting_upload": "Awaiting upload",
  "evidence.status.pending_review": "Pending review",
  "evidence.status.approved": "Approved",
  "evidence.status.rejected": "Rejected",
  "evidence.extraction.pending": "Extraction queued",
  "evidence.extraction.running": "Extracting",
  "evidence.extraction.complete": "Extracted",
  "evidence.extraction.empty": "No text found",
  "evidence.extraction.failed": "Extraction failed",
  "evidence.empty": "No evidence has been added yet.",
  "evidence.emptyBody":
    "Documents, articles, recordings and records. Nothing here reaches the advisor until a person approves an excerpt for it.",
  "evidence.search": "Filter by title",
  "evidence.review": "Review",
  "evidence.extractedText": "Extracted text",
  "evidence.warnings": "Extraction warnings",
  "evidence.proposeClaims": "Propose claims",
  "evidence.proposing": "Reading the document…",
  "evidence.proposedClaims": "Proposed claims",
  "evidence.quoteVerified": "Quote verified against document",
  "evidence.quoteUnverified": "Quote NOT found in document — do not rely on this",
  "evidence.approvedExcerpt": "Approved excerpt",
  "evidence.approvedExcerptHelp":
    "This exact text is what the advisor will read and cite. Paste it from the extracted text; do not rewrite it.",
  "evidence.useSuggested": "Use suggested excerpt",
  "evidence.reliability": "How solid the source is",
  "evidence.reliabilityNeedsApprover":
    "Primary and official-campaign tiers (and anything above secondary for OCR/transcripts) need a canonical approver.",
  "evidence.classification": "Classification",
  "evidence.officialPosition": "This is an official campaign position",
  "evidence.officialPositionHelp":
    "Only mark this when the campaign has formally adopted the content. It lets the advisor attribute positions to Chris Canales.",
  "evidence.approve": "Approve for citation",
  "evidence.reject": "Reject",
  "evidence.rejectReason": "Reason for rejection",
  "evidence.superseded": "This item has been superseded and is no longer available to the advisor.",
  "evidence.supersede": "Mark as superseded",
  "evidence.supersededBy": "Replacement evidence ID",
  "evidence.supersessionNote": "Supersession note",
  "evidence.supersessionSaved": "Supersession recorded. The older item remains in the audit archive.",
  "evidence.approved": "Approved. The advisor can now cite this.",
  "evidence.excerptNotInDoc":
    "Warning: this excerpt was not found verbatim in the extracted text. Approving it anyway will be recorded in the audit log.",
  "evidence.mediaKind": "Media",
  "evidence.modelDerived":
    "This text was produced by a model (OCR or transcription). Verify figures and names against the original before approving.",

  "work.title": "Tasks",
  "work.subtitle": "Tasks, owners, and deadlines.",
  "work.newTask": "New task",
  "work.taskTitle": "Task",
  "work.assignee": "Owner",
  "work.due": "Due",
  "work.status": "Status",
  "work.priority": "Priority",
  "work.status.todo": "To do",
  "work.status.in_progress": "In progress",
  "work.status.blocked": "Blocked",
  "work.status.done": "Done",
  "work.priority.low": "Low",
  "work.priority.normal": "Normal",
  "work.priority.high": "High",
  "work.priority.urgent": "Urgent",
  "work.empty": "No tasks yet.",
  "work.emptyBody":
    "Everything the campaign owes itself lives here: who is doing it, when it is due, and what is stuck.",
  "work.overdue": "Overdue",
  "work.unassigned": "Unassigned",
  "work.workload": "Who's carrying what",
  "work.create": "Create",

  "finance.title": "Finance and compliance",
  "finance.subtitle":
    "Every expense, every vendor, and automatic checks on both. The checks are decision support, not legal advice.",
  "finance.transactions": "Transactions",
  "finance.export": "Export CSV",
  "finance.submitExpense": "Submit expense",
  "finance.newVendor": "Add vendor",
  "finance.vendorName": "Vendor name",
  "finance.vendorCategory": "Vendor category",
  "finance.relatedParty": "Candidate, family, or related-party vendor",
  "finance.receipt": "Receipt evidence",
  "finance.receiptHelp": "Upload the receipt in Evidence before submitting the expense.",
  "finance.attendees": "Attendees",
  "finance.attendeesHelp": "Separate names with commas. Required documentation for meals.",
  "finance.country": "Country code",
  "finance.submitted": "Expense submitted for compliance screening.",
  "finance.vendorAdded": "Vendor added.",
  "finance.noVendors": "Add a vendor before submitting an expense.",
  "finance.noReceipts": "Upload receipt evidence before submitting an expense.",
  "finance.flags": "Compliance flags",
  "finance.amount": "Amount",
  "finance.vendor": "Vendor",
  "finance.category": "Category",
  "finance.date": "Date",
  "finance.purpose": "Business purpose",
  "finance.approvalStatus": "Status",
  "finance.status.pending_review": "Pending review",
  "finance.status.approved": "Approved",
  "finance.status.blocked_pending_review": "Blocked — high-severity flag",
  "finance.rule": "Rule",
  "finance.severity": "Severity",
  "finance.severity.high": "High",
  "finance.severity.medium": "Medium",
  "finance.severity.low": "Low",
  "finance.requiresCounsel": "Requires counsel review",
  "finance.resolve": "Resolve",
  "finance.resolveConfirm": "Mark this flag resolved? The action is recorded in the audit log.",
  "finance.approveWaiting": "Waiting for the automatic checks to finish.",
  "finance.approveOwn": "Someone other than the person who submitted it has to approve it.",
  "finance.approveConfirm": "Approve this expense? The action is recorded in the audit log and cannot be undone here.",
  "finance.resolution": "Resolution",
  "finance.resolution.cleared": "Cleared",
  "finance.resolution.corrected": "Corrected",
  "finance.resolution.escalated": "Escalated",
  "finance.resolution.amended_filing": "Amended filing",
  "finance.notes": "Notes",
  "finance.empty": "No transactions recorded.",
  "finance.emptyBody":
    "Every contribution and expense the campaign books, with the automatic checks that run on each one.",
  "finance.noFlags": "No open compliance flags.",
  "finance.approve": "Approve",

  "filings.title": "Campaign finance filings",
  "filings.subtitle": "Campaign-finance filing deadlines and status.",
  "filings.new": "New filing",
  "filings.name": "Filing",
  "filings.authority": "Authority",
  "filings.due": "Due",
  "filings.create": "Create",
  "filings.empty": "No filings tracked yet.",
  "filings.emptyBody":
    "Each campaign-finance report, who it goes to and when it is due, so none of them is missed.",
  "filings.status": "Status",
  "filings.statusConfirm": "Change this filing to “{status}”? The change is recorded in the audit log.",
  "filings.status.upcoming": "Upcoming",
  "filings.status.filed": "Filed",
  "filings.status.late": "Late",

  "report.title": "Weekly report",
  "report.subtitle": "A single printable snapshot — goals, open work, compliance, filings, and the watchdog pulse.",
  "report.print": "Print",
  "report.noneOpen": "Nothing open.",

  "claimsReview.title": "Approve facts",
  "claimsReview.subtitle":
    "Statements pulled out of uploaded documents. Accept what belongs in the record, reject what doesn't.",
  "claimsReview.empty": "No claims awaiting review.",
  "claimsReview.emptyBody":
    "Statements pulled out of uploaded documents land here for a person to accept or reject before they can be cited.",
  "claimsReview.accept": "Accept",
  "claimsReview.reject": "Reject",
  "claimsReview.viewEvidence": "View evidence",
  "claimsReview.quoteUnverified": "Quote not found verbatim in the source document",
  "claim.nature.established_fact": "Established fact",
  "claim.nature.reported_allegation": "Reported allegation",
  "claim.nature.subject_response": "Subject's response",
  "claim.nature.opinion": "Opinion",

  "contacts.title": "Contacts",
  "contacts.subtitle": "Voters, donors, volunteers, and press — one directory, with a logged interaction history.",
  "contacts.new": "Add contact",
  "contacts.name": "Name",
  "contacts.type": "Type",
  "contacts.phone": "Phone",
  "contacts.email": "Email",
  "contacts.notes": "Notes",
  "contacts.create": "Create",
  "contacts.empty": "No contacts yet.",
  "contacts.emptyBody":
    "Voters, donors, volunteers and press in one place, with every call and door knock logged against them.",
  "contacts.exportLoaded": "Export these {n}",
  "contacts.type.voter": "Voter",
  "contacts.type.donor": "Donor",
  "contacts.type.volunteer": "Volunteer",
  "contacts.type.press": "Press",
  "contacts.type.other": "Other",
  "contacts.export": "Export CSV",
  "contacts.interactions": "Interactions",
  "contacts.logInteraction": "Log interaction",
  "contacts.noInteractions": "No interactions logged yet.",
  "contacts.backToContacts": "Back to contacts",
  "interaction.type": "Type",
  "interaction.notes": "Notes",
  "interaction.log": "Log",
  "interaction.type.call": "Call",
  "interaction.type.text": "Text",
  "interaction.type.door": "Door knock",
  "interaction.type.email": "Email",
  "interaction.type.event": "Event",
  "interaction.type.other": "Other",

  "issues.title": "Open questions",
  "issues.subtitle": "Open questions and blockers the team is tracking.",
  "issues.new": "New issue",
  "issues.description": "Description",
  "issues.create": "Create",
  "issues.empty": "No open questions yet.",
  "issues.emptyBody":
    "Things the campaign has not settled yet — logged here so they do not get lost between people.",
  "issues.status": "Status",
  "issues.status.open": "Open",
  "issues.status.resolved": "Resolved",

  "decisions.title": "Decisions",
  "decisions.subtitle": "A record of what the campaign decided and why — so it doesn't have to be re-litigated later.",
  "decisions.new": "Record decision",
  "decisions.summary": "Decision",
  "decisions.rationale": "Rationale",
  "decisions.create": "Record",
  "decisions.empty": "No decisions recorded yet.",
  "decisions.emptyBody":
    "Write down what the campaign decided and why, so it does not have to be argued again in three weeks.",

  "risks.title": "What could go wrong",
  "risks.subtitle": "Named risks, how likely and how severe, and what mitigates each one.",
  "risks.new": "New risk",
  "risks.description": "Risk",
  "risks.severity": "Severity",
  "risks.likelihood": "Likelihood",
  "risks.mitigation": "Mitigation",
  "risks.create": "Create",
  "risks.empty": "No risks logged.",
  "risks.emptyBody":
    "Name what could go wrong, how likely it is, how bad it would be, and what the campaign is doing about it.",
  "risks.status": "Status",
  "risks.status.open": "Open",
  "risks.status.mitigated": "Mitigated",
  "risks.status.closed": "Closed",
  "risks.level.low": "Low",
  "risks.level.medium": "Medium",
  "risks.level.high": "High",

  "vendors.title": "Vendors",
  "vendors.subtitle": "Every vendor the campaign pays, and whether they're a related party.",
  "vendors.new": "Add vendor",
  "vendors.name": "Name",
  "vendors.category": "Category",
  "vendors.relatedParty": "Connected to the campaign",
  "vendors.create": "Create",
  "vendors.empty": "No vendors yet.",
  "vendors.emptyBody":
    "Everyone the campaign pays, and whether they are connected to the campaign. The filings read from this list.",

  "admin.statusActive": "Active",
  "admin.statusSuspended": "Suspended",
  "set.en": "English",
  "set.es": "Spanish",
  "role.super_admin": "Super admin",
  "role.developer": "Developer",
  "role.operations_owner": "Operations owner",
  "role.candidate": "Candidate",
  "role.campaign_manager": "Campaign manager",
  "role.chief_strategist": "Chief strategist",
  "role.treasurer": "Treasurer",
  "role.legal_counsel": "Legal counsel",
  "role.field_director": "Field director",
  "role.communications_director": "Communications director",
  "role.researcher": "Researcher",
  "role.volunteer": "Volunteer",
  "admin.forceSignOut": "End sessions",
  "admin.setup.title": "One-time setup",
  "admin.setup.run": "Run setup",
  "admin.setup.running": "Applying…",
  "admin.setup.body":
    "Gives the campaign's own operator account full campaign access (everything except Maintenance), and gives you unrestricted access including Maintenance. Safe to run more than once.",
  "admin.setup.confirm":
    "Run one-time setup? It grants the campaign operator full campaign access and gives you unrestricted access.",
  "admin.backup.title": "Data backup",
  "admin.backup.run": "Back up now",
  "admin.backup.running": "Backing up…",
  "admin.backup.body":
    "Saves a copy of all campaign data to the project's secure storage. Safe to run any time — it deletes or changes nothing.",
  "admin.suspendConfirm": "Suspend this person? They lose access until an administrator restores it.",
  "admin.roleGrantConfirm": "Give this person the {role} role?",
  "admin.roleRevokeConfirm": "Take the {role} role away from this person?",
  "admin.forceSignOutConfirm": "End all active sessions for this person? They'll need to sign in again.",
  "admin.forceSignOutDone": "Sessions ended.",

  "admin.title": "Administration",
  "admin.subtitle": "Accounts, roles, and the audit trail.",
  "admin.users": "Users",
  "admin.provision": "Provision user",
  "admin.setupLink": "One-time account setup link",
  "admin.setupLinkHelp": "Send this link securely to the user. It allows them to set their password.",
  "admin.copySetupLink": "Copy setup link",
  "admin.setupLinkCopied": "Setup link copied.",
  "admin.name": "Full name",
  "admin.email": "Email",
  "admin.roles": "Roles",
  "admin.locale": "Working language",
  "admin.jobTitle": "Title",
  "admin.effectivePermissions": "Effective permissions",
  "admin.allPermissions": "All permissions",
  "admin.editRoles": "Edit roles",
  "admin.doneEditing": "Done",
  "admin.noRoles": "No roles assigned",
  "admin.editingRolesFor": "Editing roles for {name}",
  "admin.suspend": "Suspend",
  "admin.reactivate": "Reactivate",
  "admin.audit": "Audit trail",
  "admin.action": "Action",
  "admin.actor": "Actor",
  "admin.when": "When",
  "admin.subject": "Subject",
  "admin.noAudit": "No audit events yet.",
  "admin.noAuditBody": "Every privileged action lands here — who did it, to what, and when.",
  "admin.noUsers": "No accounts yet.",
  "admin.noUsersBody": "Provision the campaign staff above. Each person gets the roles their job needs and nothing more.",

  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.close": "Close",
  "common.loadMore": "Load more",
  "common.showing": "Showing {shown}",
  "common.showingOf": "Showing {shown} of {total}",
  "billing.checkoutFailed": "Checkout could not be opened.",
  "billing.activeTitle": "Campaign subscription active",
  "billing.activateTitle": "Activate the campaign command center",
  "billing.lede": "A one-time setup fee opens the command center; one monthly fee covers service, hosting, and AI through Election Day and any runoff. Cancel any time; access runs to the end of the paid month.",
  "billing.trialActive": "Trial active until {date} or $20 in AI usage, whichever comes first.",
  "billing.trialEnded": "The trial has ended. Activate the subscription to continue.",
  "billing.verifying": "Stripe returned successfully. We are verifying the signed payment; this screen will unlock automatically.",
  "billing.canceled": "Checkout was canceled; no charge was made.",
  "billing.feature1": "Access while the subscription is active",
  "billing.feature2": "Private, backed-up campaign infrastructure",
  "billing.feature3": "Hermes, RAGFlow, and OpenViking included",
  "billing.feature4": "AI included in the monthly fee",
  "billing.accessThrough": "Access through {date}",
  "billing.maintenanceAccess": "Maintenance access",
  "billing.offer": "CAMPAIGN LAUNCH OFFER",
  "billing.timeLeft": "{days} days · {hours} hours",
  "billing.codeLabel": "ENTER AT STRIPE",
  "billing.deadline": "50% off setup: save $1,750 through September 30 at 11:59 PM El Paso time. One redemption.",
  "billing.once": "setup fee · then monthly",
  "billing.continue": "Continue to secure checkout",
  "billing.wrongAccount": "Signed in with the wrong account?",
  "billing.signOut": "Sign out",
  "billing.historyTitle": "Purchase history",
  "billing.historyEmpty": "No payments yet.",
  "billing.historyLaunch": "Setup + first month",
  "billing.historyMonthly": "Monthly service + AI",
  "billing.historyPaid": "Paid",
  "billing.historyRefunded": "Refunded",
  "billing.historyReceipt": "Receipt",
  "admin.aiLog": "AI output log",
  "admin.aiLogHint": "Every AI-generated output, as produced, with its model and a SHA-256 fingerprint. Append-only: nobody can edit or delete an entry.",
  "admin.aiLogEmpty": "No AI outputs recorded yet.",
  "admin.aiOperation": "Operation",
  "admin.aiModel": "Model",
  "admin.aiShow": "Show output",
  "admin.aiHide": "Hide",
  "billing.setupLabel": "ONE-TIME SETUP",
  "billing.monthlyLabel": "MONTHLY SERVICE + AI",
  "billing.perMonth": "/ month",
  "billing.firstCharge": "First charge today: {amount} (setup + first month).",
  "billing.canceledThrough": "Canceled. Access continues through {date}.",
  "billing.pastDue": "The last renewal failed. Update the card in Stripe before {date} to keep access.",
  "billing.includedLabel1": "SERVICE",
  "billing.included1": "Onboarding, the race analysis and playbook, and hands-on support from Elevate El Paso.",
  "billing.includedLabel2": "AI INCLUDED",
  "billing.included2": "AURA, the watchdog, OCR, and transcription are included in the monthly fee under a fair-use allowance.",
  "billing.includedLabel3": "SECURITY",
  "billing.included3": "Payments run on Stripe; the app never sees card details or holds a Stripe secret key.",
  "nav.strategy": "Analysis & strategy",
  "auth.emailOrUsername": "Email or username",
  "auth.resetEmailOnly": "Username accounts are reset by the campaign administrator.",
  "guide.startTour": "Start the guided tour",
  "pw.title": "Set your own password",
  "pw.lede": "Welcome, {name}. You signed in with a temporary password. Choose a new one to continue.",
  "pw.new": "New password",
  "pw.confirm": "Confirm new password",
  "pw.submit": "Save and continue",
  "pw.tooShort": "Use at least {n} characters.",
  "pw.mismatch": "The two passwords do not match.",
  "pw.relogin": "For security, sign out, sign back in with the temporary password, and try again.",
  "pw.failed": "The password could not be changed. Try again.",
  "welcome.eyebrow": "WELCOME",
  "welcome.title": "Welcome, {name}",
  "welcome.lede": "Two documents were prepared for you. Start with the race analysis, then read your suggested strategy. Take the tour of the whole app now, or later from the Guide.",
  "welcome.ledeGeneric": "Take a quick tour of what the app does, or explore on your own. The tour is always available from the Guide.",
  "welcome.raceAnalysis": "See the race analysis",
  "welcome.raceAnalysisHint": "The district, the voters, the issues, and all five candidates",
  "welcome.playbook": "See your suggested strategy",
  "welcome.playbookHint": "Message, platform, opponents, vulnerabilities, money, and calendar",
  "welcome.tour": "Take the tour",
  "welcome.explore": "Explore on my own",
  "tour.step": "TOUR · {n}/{total}",
  "tour.back": "Back",
  "tour.next": "Next",
  "tour.finish": "Finish",
  "tour.skip": "Skip tour",
  "tour.close": "Close",
  "strategy.title": "Analysis & strategy",
  "strategy.lede": "The District 8 race analysis and your campaign playbook, prepared by Elevate El Paso.",
  "strategy.raceAnalysis": "Race analysis",
  "strategy.playbook": "Campaign playbook",
  "strategy.contents": "Contents",
  "strategy.print": "Print",
  "strategy.missing": "This document has not been loaded yet.",
  "strategy.confidential": "Confidential. Visible only to the candidate and campaign leadership. Projections and assessments are the authors' opinion; facts are sourced in the race analysis.",
  "tour.dashboard.title": "Command center",
  "tour.dashboard.body": "Everything at a glance: the election clock, deadlines, open reviews, field activity, and narrative alerts, in priority order.",
  "tour.strategy.title": "Analysis & strategy",
  "tour.strategy.body": "Your race analysis and campaign playbook live here, always one click away. Share nothing from them publicly without checking the sources.",
  "tour.advisor.title": "Ask the campaign",
  "tour.advisor.body": "Ask any question about the race. AURA answers only from approved evidence, cites its sources, and separates facts from recommendations.",
  "tour.memory.title": "Institutional memory",
  "tour.memory.body": "Approved facts, decisions, playbook doctrine, and working notes the advisor draws on. Nothing enters without review.",
  "tour.evidence.title": "Evidence",
  "tour.evidence.body": "Every document, video, and article the campaign relies on: uploaded, transcribed, reviewed, and rated for reliability before AI can use it.",
  "tour.claims.title": "Approve facts",
  "tour.claims.body": "Approve or reject individual facts extracted from evidence. Only approved claims support the advisor's answers.",
  "tour.watchdog.title": "Watchdog",
  "tour.watchdog.body": "Monitors public coverage of every candidate three times a day and shows which stories are climbing from chatter to news.",
  "tour.warRoom.title": "War room",
  "tour.warRoom.body": "The catalog of governed tools the campaign can run, and who may use each one.",
  "tour.field.title": "Field",
  "tour.field.body": "Live crew positions (with consent), shifts, and door-to-door work.",
  "tour.contacts.title": "Contacts",
  "tour.contacts.body": "The campaign CRM: supporters, donors, volunteers, and every interaction.",
  "tour.projects.title": "Projects and work",
  "tour.projects.body": "Goals, milestones, tasks, issues, decisions, and risks: the plan turned into assignments.",
  "tour.calendar.title": "Calendar",
  "tour.calendar.body": "Your events and the crew's, in one schedule.",
  "tour.board.title": "Team board",
  "tour.board.body": "Campaign-wide messaging with @mentions.",
  "tour.report.title": "Campaign brief",
  "tour.report.body": "A decision-ready report: objectives, deadlines, risks, compliance, and progress.",
  "tour.hermes.title": "Hermes",
  "tour.hermes.body": "A drafting assistant for supervised writing jobs; every output is reviewed before use.",
  "tour.finance.title": "Finance and compliance",
  "tour.finance.body": "Expenses, vendors, filings, and automatic compliance checks, so reporting is never a scramble.",
  "tour.guide.title": "Guide",
  "tour.guide.body": "Step-by-step instructions for every screen. Restart this tour from here any time.",
  "nav.billing": "License",
  "nav.activate": "Activate your campaign",
  "hermes.title": "Drafting desk",
  "hermes.subtitle": "A supervised operations engine for research, planning, and drafting. Every result is a proposal until a person approves it.",
  "hermes.queue": "Work queue",
  "hermes.newRun": "Request a draft",
  "hermes.instructions": "Context and constraints",
  "hermes.instructionsPlaceholder": "Include the decision, deadline, and sources it should use…",
  "hermes.run": "Send to the drafting desk",
  "hermes.empty": "No drafts have been requested yet.",
  "hermes.emptyBody":
    "Ask for a morning brief, a research refresh or a bilingual draft. Every result comes back as a proposal for a person to approve.",
  "hermes.offline": "How drafts get made",
  "hermes.offlineText": "Queued work is processed only when the campaign's private drafting worker is online. CampaignOS never auto-approves host commands or publishes output.",
  "hermes.approve": "Approve proposal",
  "hermes.reject": "Reject",
  "hermes.cancel": "Request stop",
  "hermes.output": "Proposal output",
  "hermes.pending": "Awaiting human review",
  "hermes.type.morning_brief": "Morning brief",
  "hermes.type.research_refresh": "Research refresh",
  "hermes.type.task_triage": "Task triage",
  "hermes.type.source_validation": "Source validation",
  "hermes.type.meeting_prep": "Meeting prep",
  "hermes.type.bilingual_draft": "Bilingual draft",
  "common.loading": "Loading…",
  "common.error": "Something went wrong",
  "common.retry": "Retry",
  "common.none": "None",
  "common.required": "Required",
  "common.optional": "optional",
  "common.noPermission": "You do not have permission to view this.",
  "common.back": "Back",
  "search.open": "Search",
  "search.empty": "No results.",
  "search.kind.evidence": "Evidence",
  "search.kind.memory": "Memory",
  "search.kind.task": "Task",
  "search.kind.contact": "Contact",
  "common.of": "of",

  "memory.title": "What we know",
  "memory.subtitle":
    "Reviewed facts, dated events, our playbook, and notes for this week. Facts always link to a source, and the playbook is kept separate so it is never treated as fact.",
  "memory.tier": "Kind",
  "memory.tier.semantic": "Facts",
  "memory.tier.episodic": "Timeline",
  "memory.tier.procedural": "Playbook",
  "memory.tier.working": "This week's notes",
  "memory.tier.semantic.help": "Settled, reviewed facts. Must link to a source.",
  "memory.tier.episodic.help": "Dated events in the campaign's story. Must link to a source.",
  "memory.tier.procedural.help": "How this campaign does things. Used as advice, never as fact.",
  "memory.tier.working.help": "What we are focused on right now. Context only.",
  "memory.add": "Add what we know",
  "memory.statement": "Statement",
  "memory.rationale": "Rationale (why this works)",
  "memory.evidence": "Evidence IDs",
  "memory.evidenceHelp": "Source IDs, separated by commas. Required for facts and timeline entries.",
  "memory.occurredAt": "Occurred on",
  "memory.entities": "People / entities",
  "memory.clusters": "Topics",
  "memory.confidence": "Confidence",
  "memory.create": "Save",
  "memory.status": "Status",
  "memory.status.proposed": "Proposed",
  "memory.status.approved": "Approved",
  "memory.status.retired": "Retired",
  "memory.approve": "Approve",
  "memory.retire": "Retire",
  "memory.empty": "Nothing filed here yet.",
  "memory.emptyBody":
    "This is where the campaign keeps what it has checked, so the advisor and the briefing can rely on it.",
  "memory.proposedNote": "Filed as proposed. It reaches the advisor once approved.",
  "memory.doctrineNote":
    "Doctrine is usable immediately, but the advisor presents it as recommendation, never as fact.",
  "memory.working.title": "This week's notes",
  "memory.working.objectives": "Objectives (one per line)",
  "memory.working.notes": "Notes",
  "memory.working.save": "Save this week's notes",
  "memory.working.saved": "Saved. The advisor will treat this as the current focus.",

  "war.title": "Research tools",
  "war.subtitle":
    "The intelligence and analysis stack a campaign can stand up. Each tool below is described by what it does and how to use it defensibly — because for a campaign, how you run these matters as much as what they find.",
  "war.category.records": "Public-records intelligence",
  "war.category.discourse": "Public-discourse monitoring",
  "war.category.simulation": "Strategy simulation",
  "war.category.osint": "OSINT & footprint tooling",
  "war.category.knowledge": "Research & knowledge base",
  "war.what": "What it does",
  "war.use": "Campaign use",
  "war.guardrail": "Defensible-use note",
  "war.status.live": "Live",
  "war.status.recommended": "Recommended",
  "war.status.operatorRun": "Operator-run",
  "war.status.roadmap": "Roadmap",
  "war.status.caution": "Handle with counsel",
  "war.recordsIntro":
    "The defensible core. Everything here is public record — filings, votes, board memberships, published journalism — structured into the same cited evidence base the advisor reasons over. This is what actually wins arguments and survives scrutiny.",
  "war.osintIntro":
    "Powerful, dual-use, and the tools most likely to cause harm if pointed at private individuals. A campaign should run these only against itself (exposure audit) or with counsel, and never against journalists or private people. That governance is the differentiator, not the tools.",

  "war.tool.foxel.name": "Foxel",
  "war.tool.foxel.what": "Self-hosted media library with AI tagging and search over images, video, and documents.",
  "war.tool.foxel.use": "A private, searchable vault for every photo, clip, and scanned filing the campaign collects in the field — with nothing sent to a third party.",
  "war.tool.surfsense.name": "SurfSense",
  "war.tool.surfsense.what": "Private research assistant that answers questions over documents you feed it, with citations (a self-hosted NotebookLM).",
  "war.tool.surfsense.use": "Deep-read a report corpus without exposing it to a public AI service. Feeds candidate excerpts into the evidence base.",
  "war.tool.nocodb.name": "NocoDB",
  "war.tool.nocodb.what": "Open-source Airtable: a spreadsheet-database for structured records with a shareable UI.",
  "war.tool.nocodb.use": "Track donors, volunteers, turf, and vendor disclosures in structured tables that non-technical staff can edit.",
  "war.tool.spiderfoot.name": "SpiderFoot",
  "war.tool.spiderfoot.what": "Automation framework that gathers open-source intelligence about a domain, org, or digital footprint from 200+ sources.",
  "war.tool.spiderfoot.use": "Audit the campaign's own exposure: what is publicly discoverable about the candidate, staff, and infrastructure — before an opponent finds it.",
  "war.tool.reconng.name": "recon-ng",
  "war.tool.reconng.what": "Modular reconnaissance framework for open-source data gathering, similar in shape to a pentest console.",
  "war.tool.reconng.use": "Structured self-audit of the campaign's online surface. Operator-run under an engagement scope.",
  "war.tool.shodan.name": "Shodan",
  "war.tool.shodan.what": "Search engine for internet-connected devices and services.",
  "war.tool.shodan.use": "Check the campaign's own servers, cameras, and services for accidental public exposure.",
  "war.tool.sherlock.name": "Sherlock",
  "war.tool.sherlock.what": "Finds a username across hundreds of social platforms.",
  "war.tool.sherlock.use": "Brand and impersonation defense: find accounts squatting the candidate's name or impersonating the campaign.",
  "war.tool.ghunt.name": "GHunt",
  "war.tool.ghunt.what": "Investigates what a Google account exposes publicly.",
  "war.tool.ghunt.use": "Self-check what the campaign's own Google accounts leak. Not for use against private individuals.",
  "war.tool.spiderfoot.guard": "Run against your own assets under a written scope. Against third parties, only public-record facts belong in evidence.",
  "war.tool.people.guard": "Person-focused. Use only for the campaign's own exposure audit or brand defense, with counsel — never against journalists or private citizens.",
  "war.tool.betta.name": "Discourse Monitor (BettaFish-inspired)",
  "war.tool.betta.what": "Multi-agent monitoring of public political discourse, with sentiment analysis and an agent 'forum' that debates what the signal means.",
  "war.tool.betta.use": "Track how District issues move in public conversation and local media — labelled anecdotal, never as a poll. Adapted for US/local sources; the original targets Chinese platforms.",
  "war.tool.betta.guard": "Public posts only, reported as signal not measurement. No private groups, no covert profiling.",
  "war.tool.miro.name": "Message Simulator (MiroFish-inspired)",
  "war.tool.miro.what": "Agent-based simulation: model how District voter segments might react to a message before it ships — a synthetic focus group.",
  "war.tool.miro.use": "Pressure-test a reframe or an attack line against modeled segments, then test the winner with real people. Never a substitute for real voters.",
  "war.tool.miro.guard": "A rehearsal tool. Outputs are hypotheses to test, not findings — and never individualized targeting.",
  "war.tool.records.name": "Public-Records Desk",
  "war.tool.records.what": "Campaign finance filings, roll-call votes, board memberships, and published journalism, structured into the cited evidence base.",
  "war.tool.records.use": "The backbone of opposition and self research. 'Follow the money, follow the power' from documents anyone can pull — which is exactly why it survives scrutiny.",

  "watch.title": "What people are saying",
  "watch.subtitle":
    "Three times a day this checks the public web for mentions of the race — the District 8 field, the other districts on this ballot and the mayor — so the campaign hears things early. It shows what is being said and where attention is moving. It is never a poll.",
  "watch.anecdotalBanner":
    "Everything here is anecdotal public chatter and coverage, not measurement. The advisor treats it as signal, never as fact, and never cites it.",
  "watch.runNow": "Check for mentions now",
  "watch.running": "Sweeping the web…",
  "watch.lastRun": "Last sweep",
  "watch.never": "never",
  "watch.total": "Mentions tracked",
  "watch.last7d": "Last 7 days",
  "watch.last24h": "Last 24 hours",
  "watch.byEntity": "By figure",
  "watch.bySentiment": "Sentiment",
  "watch.bySource": "By source type",
  "watch.recent": "Recent mentions",
  "watch.empty": "Nothing has been picked up yet.",
  "watch.emptyBody":
    "This page collects what is being said about the race in public — news, blogs and social posts. Check now, or wait for the next automatic check.",
  "watch.sentiment.positive": "Positive",
  "watch.sentiment.neutral": "Neutral",
  "watch.sentiment.negative": "Negative",
  "watch.runResult": "Sweep complete: {fresh} new mentions ({found} found).",
  "watch.schedule": "Automatic sweeps run at 8am, 1pm, and 7pm El Paso time.",
  "watch.lastRunFailed": "Last sweep failed — check the API key",

  "watch.meter": "How far a story has travelled",
  "watch.meterHelp":
    "Each narrative's altitude on the reliability ladder, tracked over time. A topic that climbs from blog chatter into news is a breakout — the moment to respond.",
  "watch.meterEmpty": "No narratives tracked yet. They form as mentions accumulate across sweeps.",
  "watch.rung.social": "Social",
  "watch.rung.blog": "Blog",
  "watch.rung.local_news": "Local news",
  "watch.rung.established": "Established",
  "watch.rung.official_record": "Official record",
  "watch.momentum.rising": "Rising",
  "watch.momentum.steady": "Steady",
  "watch.momentum.falling": "Falling",
  "watch.breakout": "BREAKOUT to news",
  "watch.promotable": "At official record — eligible to promote to evidence",
  "watch.conduct": "Mentions that need your attention",
  "watch.conductHelp":
    "Replies from your own account to people criticising you. Standing rule: you do not argue in comments — responses go out through named staff, so nothing here turns into the story.",
  "watch.conductNone": "Nothing flagged. Keep replies going through your staff.",
  "watch.acknowledge": "Acknowledge",
  "dash.conductAlert": "Mentions that need your attention",
  "dash.conductAlertBody":
    "{n} unreviewed mention(s) about you. Talk to the campaign manager before any press.",

  "field.title": "Field map",
  "field.subtitle":
    "Live positions of crew who are sharing, so the campaign can mobilize and route the candidate. Live only — no location history is kept, and each person controls their own sharing.",
  "field.share": "Share my location",
  "field.stop": "Stop sharing / end shift",
  "field.sharingOn": "You are sharing your live location with campaign managers. It stops the moment you end your shift or close this tab, and no history is kept.",
  "field.consentTitle": "Before you share",
  "field.consentBody":
    "Sharing shows campaign managers where you are while you work the streets, so they can coordinate and send the candidate to you. It is live only — nothing is recorded after you stop. You can stop anytime.",
  "field.shiftLabel": "Shift / turf label (optional)",
  "field.asCandidate": "Share as the candidate",
  "field.denied": "Location permission was denied. Enable it in your browser to share.",
  "field.unavailable": "Your device or browser can't provide location.",
  "field.acquiring": "Getting your location…",
  "field.crewOnline": "Crew sharing now",
  "field.noneOnline": "No one is sharing their location right now.",
  "field.noneOnlineBody":
    "Crew who turn sharing on appear here while their shift is running. Nothing is kept afterwards.",
  "field.lastUpdate": "Updated",
  "field.stale": "Stale",
  "field.you": "You",
  "field.candidate": "Candidate",
  "field.directions": "Directions",
  "field.recenter": "Recenter",
  "field.accuracy": "±{m} m",
  "field.mapUnavailable": "Map failed to load.",
  "field.managerHint": "Tap a marker for who it is, when it last updated, and directions.",
  "field.crewHint": "Only campaign managers can see the map. You only control your own sharing.",

  "cal.title": "Calendar",
  "cal.subtitle": "Everything the candidate and the crew are scheduled for.",
  "cal.add": "New event",
  "cal.eventTitle": "Event",
  "cal.start": "Starts",
  "cal.end": "Ends",
  "cal.location": "Location",
  "cal.type": "Type",
  "cal.type.canvass": "Canvass",
  "cal.type.fundraiser": "Fundraiser",
  "cal.type.media": "Media / interview",
  "cal.type.debate": "Debate / forum",
  "cal.type.community": "Community event",
  "cal.type.meeting": "Meeting",
  "cal.type.deadline": "Deadline",
  "cal.audience": "For",
  "cal.audience.all": "Whole campaign",
  "cal.audience.candidate": "Candidate",
  "cal.audience.crew": "Field crew",
  "cal.notes": "Notes",
  "cal.create": "Add to calendar",
  "cal.empty": "No events scheduled.",
  "cal.emptyBody":
    "Block walks, forums, fundraisers and press — everything the candidate and the crew are expected at.",
  "cal.today": "Today",
  "cal.tomorrow": "Tomorrow",
  "cal.upcoming": "Upcoming",
  "cal.filterAll": "All",
  "cal.at": "at",

  "board.title": "Team chat",
  "board.subtitle": "Campaign chat. Tag teammates, and attach evidence, events, or tools to any message.",
  "board.channel.general": "general",
  "board.channel.field": "field",
  "board.channel.comms": "comms",
  "board.channel.strategy": "strategy",
  "board.compose": "Write a message… use @ to mention, or attach below",
  "board.send": "Send",
  "board.mention": "@ Mention",
  "board.attach": "# Attach",
  "board.mentionsMe": "Mentions me",
  "board.all": "All",
  "board.empty": "No messages yet.",
  "board.emptyBody": "This is where the team talks. Pick a channel and say the first thing.",
  "board.attachEvidence": "Evidence",
  "board.attachEvent": "Event",
  "board.attachTool": "Tool",
  "board.pickUser": "Pick a teammate",
  "board.pickEvidence": "Pick evidence",
  "board.pickEvent": "Pick an event",
  "board.pickTool": "Pick a tool",
  "board.mentioning": "Mentioning",
  "board.attached": "Attached",

  "proj.title": "The plan",
  "proj.subtitle":
    "Goals and key results drive the strategy; milestones set the timeline; the ideas board captures research before it becomes work. The best of a strategy tool and a project tool, in one place.",
  "proj.goals": "Goals & key results",
  "proj.goalsHelp": "What we are trying to achieve, and how we'll measure it.",
  "proj.addGoal": "New goal",
  "proj.objective": "Objective",
  "proj.why": "Why it matters",
  "proj.keyResults": "Key results (measurable)",
  "proj.krLabel": "Result",
  "proj.krTarget": "Target",
  "proj.krCurrent": "Current",
  "proj.addKr": "Add key result",
  "proj.goalProgress": "Progress",
  "proj.saveGoal": "Save goal",
  "proj.noGoals": "No goals yet.",
  "proj.noGoalsBody": "Say what winning looks like, and how the campaign will know it got there.",
  "proj.goal.active": "Active",
  "proj.goal.done": "Achieved",
  "proj.goal.paused": "Paused",
  "proj.milestones": "Milestones",
  "proj.milestonesHelp": "Dated checkpoints on the road to election day.",
  "proj.addMilestone": "New milestone",
  "proj.msTitle": "Milestone",
  "proj.due": "Due",
  "proj.noMilestones": "No milestones set.",
  "proj.noMilestonesBody": "Dated checkpoints on the road to election day, so slippage is visible early.",
  "proj.ms.pending": "Pending",
  "proj.ms.done": "Done",
  "proj.ms.missed": "Missed",
  "proj.linkGoal": "Linked goal",
  "proj.none": "None",
  "proj.ideas": "Ideas & research board",
  "proj.ideasHelp": "Capture a strategy idea before it's a task. Move it right as it firms up.",
  "proj.addIdea": "New idea",
  "proj.promoteToGoal": "Promote to goal →",
  "proj.ideaTitle": "Idea",
  "proj.ideaDetail": "Detail",
  "proj.tags": "Tags",
  "proj.idea.new": "New",
  "proj.idea.researching": "Researching",
  "proj.idea.validated": "Validated",
  "proj.idea.parked": "Parked",
  "proj.noIdeas": "Nothing at this stage.",
  "proj.moveTo": "Move",
  "proj.markDone": "Mark achieved",

  "set.title": "Settings",
  "set.subtitle": "Appearance, your account, the user guide, and the setup tour.",
  "set.theme": "Color theme",
  "set.theme.system": "System",
  "set.theme.light": "Light",
  "set.theme.dark": "Dark",
  "set.language": "Language",
  "set.account": "Your account",
  "set.role": "Roles",
  "set.permissions": "Effective permissions",
  "set.guide": "User guide",
  "set.guideBody": "A walkthrough of every feature and workflow.",
  "set.guideOpen": "Open the guide",
  "set.tour": "Setup tour",
  "set.tourBody": "A short guided tour of the app.",
  "set.tourStart": "Start the tour",
  "set.notify.title": "Notifications",
  "set.notify.body":
    "Emails go to {email}. Nothing is sent until you turn it on. Quiet hours: {start}–{end} El Paso time.",
  "set.notify.noEmail": "your account's email",
  "set.notify.briefing": "Morning briefing email (7:00 a.m.)",
  "set.notify.briefingHint": "Days to Election Day, money and supporters, your tasks, the next event.",
  "set.notify.media": "Urgent media opportunity alerts",
  "set.notify.mediaHint": "Only for opportunities marked Now. During quiet hours they wait for the 7:00 briefing.",
  "set.notify.saving": "Saving…",
  "set.notify.saved": "Saved.",
  "set.notify.saveFailed": "Could not save: {error}",
  "set.notify.test": "Send me a test email",
  "set.notify.testing": "Sending…",
  "set.notify.testSent": "Test email sent to {email}.",
  "set.notify.testFailed": "Test email not sent: {error}",

  "asst.title": "Next best move",
  "asst.subtitle": "Suggestions from your live campaign data.",
  "asst.none": "You're on top of it — nothing urgent right now.",
  "asst.go": "Go",
  "asst.s.conduct": "{n} unreviewed mention(s) about you. Talk to the campaign manager before any press.",
  "asst.s.breakout": "A story broke into news coverage. Brief the team.",
  "asst.s.election": "{n} day(s) to the election.",
  "asst.s.runoff": "{n} day(s) to the runoff.",
  "asst.s.noEvidence": "The advisor has nothing it can cite yet. Approve a source to make it useful.",
  "asst.s.pending": "{n} document(s) await review. Approve them so the advisor can cite them.",
  "asst.s.flags": "{n} open compliance flag(s).",
  "asst.s.objectives": "Set this week's objectives to focus the advisor.",
  "asst.s.goals": "Set a campaign goal with key results.",
  "asst.s.overdue": "{n} overdue task(s).",
  "asst.dismiss": "Dismiss",
  "asst.urgent": "{n} need(s) attention now",

  "wiz.skip": "Skip",
  "wiz.next": "Next",
  "wiz.back": "Back",
  "wiz.done": "Finish",
  "wiz.step": "Step {n} of {total}",
} as const;

// Candidate-tool strings live with each feature (web/src/features/*/strings.ts)
// so the feature owns its keys without editing this file. Each module's keys
// carry its prefix; web/src/features/i18n-features.test.ts fails on any key
// collision, because a later spread would silently overwrite an earlier one.
const en = {
  ...baseEn,
  ...briefingEn,
  ...callTimeEn,
  ...practiceEn,
  ...mediaScoutEn,
} as const;

export type StringKey = keyof typeof en;

const baseEs = {
  "app.name": "Canales CampaignOS",
  "app.tagline": "Chris Canales · Concejo Municipal de El Paso, Distrito 8 · 2026",

  "nav.dashboard": "Resumen de la campaña",
  "nav.advisor": "Pregúntale a la campaña",
  "nav.evidence": "Evidencia",
  "nav.work": "Tareas",
  "nav.finance": "Finanzas",
  "nav.filings": "Declaraciones de finanzas",
  "nav.contacts": "Contactos",
  "nav.issues": "Preguntas abiertas",
  "nav.decisions": "Decisiones",
  "nav.risks": "Riesgos",
  "nav.vendors": "Proveedores",
  "nav.claimsReview": "Aprobar hechos",
  "nav.report": "Informe semanal",
  "nav.admin": "Administración",
  "nav.memory": "Lo que sabemos",
  "nav.warRoom": "Herramientas de investigación",
  "nav.watchdog": "Lo que se está diciendo",
  "nav.field": "Mapa de campo",
  "nav.calendar": "Calendario",
  "nav.board": "Chat del equipo",
  "nav.projects": "Plan",
  "nav.settings": "Ajustes",
  "nav.guide": "Guía",
  "nav.console": "Mantenimiento",
  "nav.hermes": "Mesa de redacción",
  "nav.signOut": "Cerrar sesión",
  "nav.menu": "Abrir el menú",
  "console.title": "Mantenimiento",
  "console.subtitle":
    "Superficie de mantenimiento autenticada. Cada comando pasa por permisos y queda en la bitácora de auditoría.",
  "console.ready": "Consola lista. Escribe 'help'.",
  "console.command": "Comando de mantenimiento",
  "console.run": "Ejecutar",
  "console.log": "Salida del comando",
  "console.destructiveConfirm":
    "¿Ejecutar {command}? Esto borra datos y no se puede deshacer.",
  "nav.more": "Más",
  "tab.today": "Hoy",
  "tab.overview": "Resumen",
  "tab.calls": "Llamadas",
  "tab.practice": "Práctica",
  "tab.media": "Medios",
  "tab.tasks": "Tareas",
  "tab.calendar": "Agenda",
  "tab.chat": "Chat",
  "nav.primary": "Secciones principales",
  "nav.language": "Idioma",
  "nav.today": "Mi campaña hoy",
  "nav.callTime": "Llamadas a donantes",
  "nav.practice": "Práctica de respuestas",
  "nav.mediaScout": "Radar de medios",
  "common.comingSoon": "Esta herramienta se está preparando.",

  "auth.title": "Iniciar sesión",
  "auth.platformOverview": "Vista general de la plataforma",
  "auth.resetPassword": "Establecer o restablecer contraseña",
  "auth.demoKicker": "Demo · solo lectura",
  "auth.demoTitle": "Explorar la demostración",
  "auth.demoBody": "Datos ficticios y aislados. Nunca toca la campaña real.",
  "auth.demoLaunch": "Entrar al demo",
  "auth.demoOpening": "Abriendo…",
  "auth.resetSent": "Si el correo pertenece a una cuenta, recibirás un enlace seguro para establecer la contraseña.",
  "auth.resetFailed":
    "No se pudo enviar el enlace: es un problema del sitio, no de tu cuenta. Avisa a quien lo configuró.",
  "auth.subtitle": "Sistema interno de campaña. Solo personal autorizado.",
  "auth.email": "Correo electrónico",
  "auth.password": "Contraseña",
  "auth.submit": "Entrar",
  "auth.working": "Entrando…",
  "auth.failed": "No se pudo iniciar sesión. Revisa tu correo y contraseña.",
  "auth.noAccount":
    "No existe un registro de usuario para ti. Pide a un administrador que te dé acceso.",
  "auth.suspended": "Esta cuenta está suspendida. Contacta a un administrador.",
  "auth.wrongCampaign":
    "Esta cuenta pertenece a otra campaña. Canales CampaignOS es exclusivamente para la campaña de Chris Canales al Concejo Municipal de El Paso, Distrito 8.",

  "dash.greeting": "Qué gusto verte, {name}",
  "dash.role": "Rol",
  "dash.strategistTitle": "Centro de mando estratégico",
  "dash.evidenceBase": "Base de evidencia",
  "dash.approvedEvidence": "Aprobada y citable",
  "dash.pendingReview": "En espera de revisión",
  "dash.pendingSources": "Registrada, aún no inspeccionada",
  "dash.recentAnswers": "Respuestas recientes del asesor",
  "dash.openFlags": "Alertas de cumplimiento abiertas",
  "dash.overdueTasks": "Trabajo vencido",
  "dash.upcomingFilings": "Próximos informes",
  "dash.filingLate": "Atrasado",
  "dash.filingDueIn": "Vence en {n}d",
  "dash.noAnswers": "Aún no hay respuestas del asesor.",
  "dash.researchGaps": "Investigación pendiente",
  "dash.researchGapsHelp":
    "Fuentes que la campaña registró pero aún no ha inspeccionado. El asesor no las citará.",
  "dash.askAdvisor": "Pregúntale a la campaña",
  "dash.action.title": "Empieza aquí",
  "dash.action.reviewFlags": "Revisar {n} alerta(s)",
  "dash.action.openMap": "Abrir el mapa de campo",
  "dash.action.openFinance": "Abrir finanzas",
  "dash.action.myTasks": "Ver mis tareas",
  "dash.uploadEvidence": "Agregar evidencia",
  "dash.issueCoverage": "Evidencia por eje temático",
  "dash.noCoverage": "Todavía no hay evidencia aprobada asignada a ningún eje temático.",
  "dash.commandCenter": "Resumen de la campaña",
  "dash.daysToElection": "Días para la elección",
  "dash.daysToFiling": "Días para el cierre de registro",
  "dash.electionDay": "Elección · 3 de noviembre de 2026",
  "dash.daysToRunoff": "Días para la segunda vuelta",
  "dash.runoffDay": "Segunda vuelta · 12 de diciembre de 2026",
  "dash.runoffOver": "La segunda vuelta ya pasó.",
  "dash.filingClose": "Registro cierra · 17 de agosto de 2026",
  "dash.filingClosed": "Registro cerrado",
  "dash.electionPassed": "Día de la elección",
  "dash.days": "días",
  "dash.knowledge": "Elementos de conocimiento",
  "dash.mentions7d": "Menciones (7d)",
  "dash.watchdogPulse": "Lo que se está diciendo",
  "dash.sentiment": "Sentimiento de las menciones rastreadas",
  "dash.climbing": "Historias ganando cobertura",
  "dash.breakoutAlert": "Saltando a noticia",
  "dash.nothingClimbing": "Ninguna narrativa ha saltado a cobertura de noticias.",
  "dash.objectives": "Objetivos actuales",
  "dash.noObjectives": "Todavía no hay objetivos para esta semana.",
  "dash.setObjectives": "Definir los objetivos de la semana",
  "dash.openWatchdog": "Ver lo que se está diciendo",
  "dash.runSweep": "Busca menciones para llenar esta sección.",

  "advisor.title": "Pregúntale a la campaña",
  "advisor.subtitle":
    "Las respuestas se construyen únicamente con evidencia aprobada en este sistema. Cada afirmación factual lleva su cita, y las afirmaciones que no pasan la verificación se eliminan antes de que las veas.",
  "advisor.convertToTask": "Convertir en tarea →",
  "advisor.taskAdded": "Agregada a Trabajo",
  "advisor.question": "Pregunta",
  "advisor.questionPlaceholder":
    "¿Qué establece nuestra evidencia sobre la seguridad pública en el centro del Distrito 8?",
  "advisor.purpose": "Propósito",
  "advisor.purpose.research": "Investigación",
  "advisor.purpose.briefing": "Informe",
  "advisor.purpose.operations": "Operaciones",
  "advisor.purpose.communications": "Comunicación",
  "advisor.purpose.field": "Territorio",
  "advisor.purpose.compliance_support": "Apoyo a cumplimiento",
  "advisor.issueCluster": "Tema",
  "advisor.allClusters": "Todos los temas",
  "advisor.ask": "Preguntar",
  "advisor.thinking": "Recuperando evidencia y razonando…",
  "advisor.summary": "Resumen",
  "advisor.claims": "Afirmaciones",
  "advisor.sources": "Fuentes utilizadas",
  "advisor.modelAuthoredNote":
    "Redactado por el modelo; no verificado contra la evidencia. Trátalo como una pauta de investigación, no como un hallazgo.",
  "advisor.gaps": "Vacíos de evidencia",
  "advisor.required": "Fuentes que cerrarían estos vacíos",
  "advisor.noAnswerTitle": "No se puede responder con la evidencia actual",
  "advisor.quarantined": "Retenido — la fuente no lo respalda",
  "advisor.quarantinedHelp":
    "Estas afirmaciones no pasaron la verificación mecánica de citas y fueron retenidas. Se muestran por transparencia, no para usarse.",
  "advisor.injection": "Se detectó un intento de inyección de instrucciones en el material fuente",
  "advisor.verified": "Verificado",
  "advisor.verifiedHelp": "Todas las afirmaciones se resolvieron contra la evidencia suministrada.",
  "advisor.partial": "Verificado parcialmente",
  "advisor.partialHelp": "Algunas afirmaciones fueron retenidas. Ver abajo.",
  "advisor.failed": "No pasó la verificación",
  "advisor.outOfScope": "Fuera de alcance",
  "advisor.noEvidence": "Sin evidencia",
  "advisor.refused": "Rechazado",
  "advisor.provider": "Modelo",
  "advisor.excluded": "Evidencia considerada y excluida",
  "advisor.markReviewed": "Marcar como revisada",
  "advisor.accepted": "Aceptar",
  "advisor.rejected": "Rechazar",
  "advisor.needsWork": "Requiere trabajo",
  "advisor.reviewSaved": "Revisión registrada.",
  "advisor.history": "Preguntas recientes",

  "claim.fact": "Hecho",
  "claim.allegation": "Señalamiento",
  "claim.response": "Respuesta",
  "claim.inference": "Inferencia",
  "claim.recommendation": "Recomendación",
  "claim.confidence": "Confianza",
  "claim.high": "Alta",
  "claim.medium": "Media",
  "claim.low": "Baja",

  "reliability.primary": "Primaria",
  "reliability.official_campaign": "Campaña oficial",
  "reliability.secondary": "Secundaria",
  "reliability.anecdotal": "Anecdótica",
  "reliability.pending": "Sin revisar todavía",

  "aura.outcome.verified": "Verificado",
  "aura.outcome.verified_with_quarantine": "Verificado parcialmente",
  "aura.outcome.failed_verification": "Verificación fallida",
  "aura.outcome.refused_on_retry": "Rechazada en el reintento",
  "aura.outcome.no_evidence": "Sin evidencia",
  "aura.outcome.out_of_scope": "Fuera de alcance",
  "aura.outcome.refused": "Rechazado",

  "compliance.rule.RELATED_PARTY": "Pago a parte relacionada",
  "compliance.rule.POSSIBLE_PERSONAL_USE": "Posible uso personal",
  "compliance.rule.MEAL_WITHOUT_ATTENDEES": "Comida sin lista de asistentes",
  "compliance.rule.FOREIGN_LOCATION": "Ubicación en el extranjero",
  "compliance.rule.MISSING_RECEIPT": "Falta comprobante",
  "compliance.rule.POSSIBLE_DUPLICATE": "Posible duplicado",
  "compliance.rule.THIN_BUSINESS_PURPOSE": "Propósito comercial insuficiente",

  "cluster.government_finance": "Finanzas de gobierno",
  "cluster.public_safety": "Seguridad pública",
  "cluster.transportation": "Transporte",
  "cluster.housing": "Vivienda",
  "cluster.built_environment": "Entorno construido",
  "cluster.economic_development": "Desarrollo económico",
  "cluster.preservation": "Preservación",
  "cluster.neighborhood_services": "Servicios de vecindario",

  "mention.sourceType.news": "Noticia",
  "mention.sourceType.blog": "Blog",
  "mention.sourceType.social": "Redes sociales",
  "mention.sourceType.official": "Oficial",
  "mention.sourceType.forum": "Foro",
  "mention.sourceType.other": "Otro",

  "evidence.title": "Evidencia",
  "evidence.subtitle":
    "Nada de esto llega al asesor hasta que una persona apruebe un extracto.",
  "evidence.add": "Agregar evidencia",
  "evidence.docTitle": "Título",
  "evidence.file": "Archivo",
  "evidence.sourceType": "Tipo de fuente",
  "evidence.publisher": "Editor o emisor",
  "evidence.publishedDate": "Fecha de publicación",
  "evidence.sourceUrl": "URL de la fuente",
  "evidence.geography": "Geografía",
  "evidence.clusters": "Temas",
  "evidence.rights": "Nota de derechos y permisos",
  "evidence.upload": "Subir",
  "evidence.uploading": "Subiendo… {percent}%",
  "evidence.extracting":
    "Extrayendo texto. Puede tardar varios minutos en audio y video.",
  "evidence.status": "Estado",
  "evidence.status.awaiting_upload": "Esperando archivo",
  "evidence.status.pending_review": "Pendiente de revisión",
  "evidence.status.approved": "Aprobada",
  "evidence.status.rejected": "Rechazada",
  "evidence.extraction.pending": "Extracción en cola",
  "evidence.extraction.running": "Extrayendo",
  "evidence.extraction.complete": "Texto extraído",
  "evidence.extraction.empty": "No se encontró texto",
  "evidence.extraction.failed": "Falló la extracción",
  "evidence.empty": "Todavía no se ha agregado evidencia.",
  "evidence.emptyBody":
    "Documentos, artículos, grabaciones y registros. Nada de esto llega al asesor hasta que una persona aprueba un extracto.",
  "evidence.search": "Filtrar por título",
  "evidence.review": "Revisar",
  "evidence.extractedText": "Texto extraído",
  "evidence.warnings": "Advertencias de extracción",
  "evidence.proposeClaims": "Proponer afirmaciones",
  "evidence.proposing": "Leyendo el documento…",
  "evidence.proposedClaims": "Afirmaciones propuestas",
  "evidence.quoteVerified": "Cita verificada contra el documento",
  "evidence.quoteUnverified":
    "La cita NO se encontró en el documento — no confíes en esta",
  "evidence.approvedExcerpt": "Extracto aprobado",
  "evidence.approvedExcerptHelp":
    "Este texto exacto es lo que el asesor leerá y citará. Pégalo desde el texto extraído; no lo reescribas.",
  "evidence.useSuggested": "Usar extracto sugerido",
  "evidence.reliability": "Qué tan sólida es la fuente",
  "evidence.reliabilityNeedsApprover":
    "Los niveles primario y oficial de campaña (y cualquier nivel superior a secundario para OCR/transcripciones) requieren un aprobador canónico.",
  "evidence.classification": "Clasificación",
  "evidence.officialPosition": "Esta es una posición oficial de la campaña",
  "evidence.officialPositionHelp":
    "Marca esto solo cuando la campaña haya adoptado formalmente el contenido. Permite al asesor atribuir posiciones a Chris Canales.",
  "evidence.approve": "Aprobar para citación",
  "evidence.reject": "Rechazar",
  "evidence.rejectReason": "Motivo del rechazo",
  "evidence.superseded": "Este elemento fue sustituido y ya no está disponible para el asesor.",
  "evidence.supersede": "Marcar como sustituido",
  "evidence.supersededBy": "ID de la evidencia de reemplazo",
  "evidence.supersessionNote": "Nota de sustitución",
  "evidence.supersessionSaved": "Sustitución registrada. El elemento anterior permanece en el archivo de auditoría.",
  "evidence.approved": "Aprobada. El asesor ya puede citarla.",
  "evidence.excerptNotInDoc":
    "Advertencia: este extracto no se encontró textualmente en el texto extraído. Aprobarlo de todas formas quedará registrado en la bitácora de auditoría.",
  "evidence.mediaKind": "Medio",
  "evidence.modelDerived":
    "Este texto fue producido por un modelo (OCR o transcripción). Verifica cifras y nombres contra el original antes de aprobar.",

  "work.title": "Tareas",
  "work.subtitle": "Tareas, responsables y plazos.",
  "work.newTask": "Nueva tarea",
  "work.taskTitle": "Tarea",
  "work.assignee": "Responsable",
  "work.due": "Vence",
  "work.status": "Estado",
  "work.priority": "Prioridad",
  "work.status.todo": "Por hacer",
  "work.status.in_progress": "En curso",
  "work.status.blocked": "Bloqueada",
  "work.status.done": "Hecha",
  "work.priority.low": "Baja",
  "work.priority.normal": "Normal",
  "work.priority.high": "Alta",
  "work.priority.urgent": "Urgente",
  "work.empty": "Todavía no hay tareas.",
  "work.emptyBody":
    "Aquí vive todo lo que la campaña se debe a sí misma: quién lo hace, cuándo vence y qué está atorado.",
  "work.overdue": "Vencida",
  "work.unassigned": "Sin asignar",
  "work.workload": "Quién lleva qué",
  "work.create": "Crear",

  "finance.title": "Finanzas y cumplimiento",
  "finance.subtitle":
    "Cada gasto, cada proveedor, y revisiones automáticas de ambos. Las revisiones son apoyo a la decisión, no asesoría legal.",
  "finance.transactions": "Transacciones",
  "finance.export": "Exportar CSV",
  "finance.submitExpense": "Enviar gasto",
  "finance.newVendor": "Agregar proveedor",
  "finance.vendorName": "Nombre del proveedor",
  "finance.vendorCategory": "Categoría del proveedor",
  "finance.relatedParty": "Proveedor del candidato, familiar o parte relacionada",
  "finance.receipt": "Evidencia del recibo",
  "finance.receiptHelp": "Sube el recibo en Evidencia antes de enviar el gasto.",
  "finance.attendees": "Asistentes",
  "finance.attendeesHelp": "Separa los nombres con comas. Es documentación requerida para comidas.",
  "finance.country": "Código de país",
  "finance.submitted": "Gasto enviado para revisión de cumplimiento.",
  "finance.vendorAdded": "Proveedor agregado.",
  "finance.noVendors": "Agrega un proveedor antes de enviar un gasto.",
  "finance.noReceipts": "Sube evidencia del recibo antes de enviar un gasto.",
  "finance.flags": "Alertas de cumplimiento",
  "finance.amount": "Monto",
  "finance.vendor": "Proveedor",
  "finance.category": "Categoría",
  "finance.date": "Fecha",
  "finance.purpose": "Propósito de campaña",
  "finance.approvalStatus": "Estado",
  "finance.status.pending_review": "Pendiente de revisión",
  "finance.status.approved": "Aprobada",
  "finance.status.blocked_pending_review": "Bloqueada — alerta de severidad alta",
  "finance.rule": "Regla",
  "finance.severity": "Severidad",
  "finance.severity.high": "Alta",
  "finance.severity.medium": "Media",
  "finance.severity.low": "Baja",
  "finance.requiresCounsel": "Requiere revisión legal",
  "finance.resolve": "Resolver",
  "finance.resolveConfirm": "¿Marcar esta alerta como resuelta? La acción queda en la bitácora de auditoría.",
  "finance.approveWaiting": "Esperando a que terminen las revisiones automáticas.",
  "finance.approveOwn": "Debe aprobarlo alguien distinto de quien lo envió.",
  "finance.approveConfirm": "¿Aprobar este gasto? La acción queda en la bitácora de auditoría y no se puede deshacer aquí.",
  "finance.resolution": "Resolución",
  "finance.resolution.cleared": "Descartada",
  "finance.resolution.corrected": "Corregida",
  "finance.resolution.escalated": "Escalada",
  "finance.resolution.amended_filing": "Informe enmendado",
  "finance.notes": "Notas",
  "finance.empty": "No hay transacciones registradas.",
  "finance.emptyBody":
    "Cada contribución y cada gasto que registra la campaña, con las revisiones automáticas que corren sobre cada uno.",
  "finance.noFlags": "No hay alertas de cumplimiento abiertas.",
  "finance.approve": "Aprobar",

  "filings.title": "Declaraciones de finanzas",
  "filings.subtitle": "Plazos y estado de los informes de finanzas de campaña.",
  "filings.new": "Nuevo informe",
  "filings.name": "Informe",
  "filings.authority": "Autoridad",
  "filings.due": "Vence",
  "filings.create": "Crear",
  "filings.empty": "Aún no hay declaraciones registradas.",
  "filings.emptyBody":
    "Cada reporte de finanzas de campaña, a quién va y cuándo vence, para que no se escape ninguno.",
  "filings.status": "Estado",
  "filings.statusConfirm": "¿Cambiar esta declaración a “{status}”? El cambio queda en la bitácora de auditoría.",
  "filings.status.upcoming": "Próximo",
  "filings.status.filed": "Presentado",
  "filings.status.late": "Atrasado",

  "report.title": "Informe semanal",
  "report.subtitle": "Una sola vista imprimible — objetivos, trabajo abierto, cumplimiento, informes y el pulso del vigilante.",
  "report.print": "Imprimir",
  "report.noneOpen": "Nada pendiente.",

  "claimsReview.title": "Aprobar hechos",
  "claimsReview.subtitle":
    "Frases extraídas de documentos subidos. Acepta lo que pertenece al registro, rechaza lo que no.",
  "claimsReview.empty": "No hay afirmaciones pendientes de revisión.",
  "claimsReview.emptyBody":
    "Las frases extraídas de documentos subidos llegan aquí para que una persona las acepte o rechace antes de poder citarlas.",
  "claimsReview.accept": "Aceptar",
  "claimsReview.reject": "Rechazar",
  "claimsReview.viewEvidence": "Ver evidencia",
  "claimsReview.quoteUnverified": "La cita no se encontró de forma exacta en el documento fuente",
  "claim.nature.established_fact": "Hecho establecido",
  "claim.nature.reported_allegation": "Señalamiento reportado",
  "claim.nature.subject_response": "Respuesta del sujeto",
  "claim.nature.opinion": "Opinión",

  "contacts.title": "Contactos",
  "contacts.subtitle": "Votantes, donantes, voluntarios y prensa — un solo directorio, con historial de interacciones.",
  "contacts.new": "Agregar contacto",
  "contacts.name": "Nombre",
  "contacts.type": "Tipo",
  "contacts.phone": "Teléfono",
  "contacts.email": "Correo",
  "contacts.notes": "Notas",
  "contacts.create": "Crear",
  "contacts.empty": "Aún no hay contactos.",
  "contacts.emptyBody":
    "Votantes, donantes, voluntariado y prensa en un solo lugar, con cada llamada y cada puerta registradas.",
  "contacts.exportLoaded": "Exportar estos {n}",
  "contacts.type.voter": "Votante",
  "contacts.type.donor": "Donante",
  "contacts.type.volunteer": "Voluntario",
  "contacts.type.press": "Prensa",
  "contacts.type.other": "Otro",
  "contacts.export": "Exportar CSV",
  "contacts.interactions": "Interacciones",
  "contacts.logInteraction": "Registrar interacción",
  "contacts.noInteractions": "Aún no hay interacciones registradas.",
  "contacts.backToContacts": "Volver a contactos",
  "interaction.type": "Tipo",
  "interaction.notes": "Notas",
  "interaction.log": "Registrar",
  "interaction.type.call": "Llamada",
  "interaction.type.text": "Mensaje de texto",
  "interaction.type.door": "Visita a puerta",
  "interaction.type.email": "Correo",
  "interaction.type.event": "Evento",
  "interaction.type.other": "Otro",

  "issues.title": "Preguntas abiertas",
  "issues.subtitle": "Preguntas abiertas y bloqueos que el equipo está siguiendo.",
  "issues.new": "Nuevo pendiente",
  "issues.description": "Descripción",
  "issues.create": "Crear",
  "issues.empty": "Aún no hay preguntas abiertas.",
  "issues.emptyBody":
    "Lo que la campaña todavía no resuelve — registrado aquí para que no se pierda entre personas.",
  "issues.status": "Estado",
  "issues.status.open": "Abierto",
  "issues.status.resolved": "Resuelto",

  "decisions.title": "Decisiones",
  "decisions.subtitle": "Un registro de lo que decidió la campaña y por qué — para no tener que volver a discutirlo después.",
  "decisions.new": "Registrar decisión",
  "decisions.summary": "Decisión",
  "decisions.rationale": "Justificación",
  "decisions.create": "Registrar",
  "decisions.empty": "Aún no hay decisiones registradas.",
  "decisions.emptyBody":
    "Anota qué decidió la campaña y por qué, para no volver a discutirlo en tres semanas.",

  "risks.title": "Qué podría salir mal",
  "risks.subtitle": "Riesgos identificados, su probabilidad, su severidad y qué los mitiga.",
  "risks.new": "Nuevo riesgo",
  "risks.description": "Riesgo",
  "risks.severity": "Severidad",
  "risks.likelihood": "Probabilidad",
  "risks.mitigation": "Mitigación",
  "risks.create": "Crear",
  "risks.empty": "No hay riesgos registrados.",
  "risks.emptyBody":
    "Nombra qué podría salir mal, qué tan probable es, qué tan grave sería y qué está haciendo la campaña al respecto.",
  "risks.status": "Estado",
  "risks.status.open": "Abierto",
  "risks.status.mitigated": "Mitigado",
  "risks.status.closed": "Cerrado",
  "risks.level.low": "Baja",
  "risks.level.medium": "Media",
  "risks.level.high": "Alta",

  "vendors.title": "Proveedores",
  "vendors.subtitle": "Cada proveedor al que paga la campaña, y si es parte relacionada.",
  "vendors.new": "Agregar proveedor",
  "vendors.name": "Nombre",
  "vendors.category": "Categoría",
  "vendors.relatedParty": "Vinculado a la campaña",
  "vendors.create": "Crear",
  "vendors.empty": "Aún no hay proveedores.",
  "vendors.emptyBody":
    "Todos a quienes paga la campaña, y si están vinculados a ella. Las declaraciones se arman con esta lista.",

  "admin.statusActive": "Activa",
  "admin.statusSuspended": "Suspendida",
  "set.en": "Inglés",
  "set.es": "Español",
  "role.super_admin": "Súper administrador",
  "role.developer": "Desarrollador",
  "role.operations_owner": "Responsable de operaciones",
  "role.candidate": "Candidato",
  "role.campaign_manager": "Gerencia de campaña",
  "role.chief_strategist": "Jefa de estrategia",
  "role.treasurer": "Tesorería",
  "role.legal_counsel": "Asesoría legal",
  "role.field_director": "Dirección de campo",
  "role.communications_director": "Dirección de comunicación",
  "role.researcher": "Investigación",
  "role.volunteer": "Voluntariado",
  "admin.forceSignOut": "Cerrar sesiones",
  "admin.setup.title": "Configuración única",
  "admin.setup.run": "Ejecutar configuración",
  "admin.setup.running": "Aplicando…",
  "admin.setup.body":
    "Da a la cuenta operadora de la campaña acceso total a la campaña (todo menos Mantenimiento), y a ti acceso sin restricciones, incluido Mantenimiento. Se puede ejecutar más de una vez sin riesgo.",
  "admin.setup.confirm":
    "¿Ejecutar la configuración única? Da acceso total de campaña a la cuenta operadora y a ti acceso sin restricciones.",
  "admin.backup.title": "Respaldo de datos",
  "admin.backup.run": "Respaldar ahora",
  "admin.backup.running": "Respaldando…",
  "admin.backup.body":
    "Guarda una copia de todos los datos de la campaña en el almacenamiento seguro del proyecto. Seguro de ejecutar en cualquier momento — no borra ni modifica nada.",
  "admin.suspendConfirm": "¿Suspender a esta persona? Pierde el acceso hasta que una administradora lo restablezca.",
  "admin.roleGrantConfirm": "¿Darle a esta persona el rol {role}?",
  "admin.roleRevokeConfirm": "¿Quitarle a esta persona el rol {role}?",
  "admin.forceSignOutConfirm": "¿Cerrar todas las sesiones activas de esta persona? Tendrá que iniciar sesión de nuevo.",
  "admin.forceSignOutDone": "Sesiones cerradas.",

  "admin.title": "Administración",
  "admin.subtitle": "Cuentas, roles y bitácora de auditoría.",
  "admin.users": "Usuarios",
  "admin.provision": "Dar de alta usuario",
  "admin.setupLink": "Enlace único para configurar la cuenta",
  "admin.setupLinkHelp": "Envía este enlace de forma segura al usuario. Le permite establecer su contraseña.",
  "admin.copySetupLink": "Copiar enlace",
  "admin.setupLinkCopied": "Enlace copiado.",
  "admin.name": "Nombre completo",
  "admin.email": "Correo electrónico",
  "admin.roles": "Roles",
  "admin.locale": "Idioma de trabajo",
  "admin.jobTitle": "Cargo",
  "admin.effectivePermissions": "Permisos efectivos",
  "admin.allPermissions": "Todos los permisos",
  "admin.editRoles": "Editar roles",
  "admin.doneEditing": "Listo",
  "admin.noRoles": "Sin roles asignados",
  "admin.editingRolesFor": "Editando roles de {name}",
  "admin.suspend": "Suspender",
  "admin.reactivate": "Reactivar",
  "admin.audit": "Bitácora de auditoría",
  "admin.action": "Acción",
  "admin.actor": "Autor",
  "admin.when": "Cuándo",
  "admin.subject": "Objeto",
  "admin.noAudit": "Todavía no hay eventos de auditoría.",
  "admin.noAuditBody": "Cada acción privilegiada aterriza aquí: quién la hizo, sobre qué y cuándo.",
  "admin.noUsers": "Aún no hay cuentas.",
  "admin.noUsersBody": "Da de alta arriba al personal de la campaña. Cada persona recibe los roles que su trabajo necesita y nada más.",

  "common.cancel": "Cancelar",
  "common.save": "Guardar",
  "common.close": "Cerrar",
  "common.loadMore": "Cargar más",
  "common.showing": "Mostrando {shown}",
  "common.showingOf": "Mostrando {shown} de {total}",
  "billing.checkoutFailed": "No se pudo abrir el pago.",
  "billing.activeTitle": "Suscripción de campaña activa",
  "billing.activateTitle": "Active el centro de mando",
  "billing.lede": "Una cuota única de instalación abre el centro de mando; una mensualidad cubre servicio, hospedaje e IA hasta el día de la elección y cualquier segunda vuelta. Cancele cuando quiera; el acceso sigue hasta el fin del mes pagado.",
  "billing.trialActive": "Prueba activa hasta {date} o hasta consumir $20 en IA, lo que ocurra primero.",
  "billing.trialEnded": "La prueba terminó. Active la suscripción para continuar.",
  "billing.verifying": "Stripe confirmó el regreso. Estamos verificando el pago firmado; esta pantalla se abrirá automáticamente.",
  "billing.canceled": "El pago fue cancelado; no se realizó ningún cargo.",
  "billing.feature1": "Acceso mientras la suscripción esté activa",
  "billing.feature2": "Infraestructura de campaña privada y respaldada",
  "billing.feature3": "Hermes, RAGFlow y OpenViking incluidos",
  "billing.feature4": "IA incluida en la mensualidad",
  "billing.accessThrough": "Acceso hasta {date}",
  "billing.maintenanceAccess": "Acceso de mantenimiento",
  "billing.offer": "OFERTA DE LANZAMIENTO",
  "billing.timeLeft": "{days} días · {hours} horas",
  "billing.codeLabel": "CÓDIGO EN STRIPE",
  "billing.deadline": "50% de descuento en la instalación: ahorre $1,750 hasta el 30 de septiembre a las 11:59 PM, hora de El Paso. Una redención.",
  "billing.once": "instalación · luego mensualidad",
  "billing.continue": "Continuar al pago seguro",
  "billing.wrongAccount": "¿Entró con la cuenta equivocada?",
  "billing.signOut": "Cerrar sesión",
  "billing.historyTitle": "Historial de compras",
  "billing.historyEmpty": "Todavía no hay pagos.",
  "billing.historyLaunch": "Instalación + primer mes",
  "billing.historyMonthly": "Servicio mensual + IA",
  "billing.historyPaid": "Pagado",
  "billing.historyRefunded": "Reembolsado",
  "billing.historyReceipt": "Recibo",
  "admin.aiLog": "Registro de resultados de IA",
  "admin.aiLogHint": "Cada resultado generado por IA, tal como se produjo, con su modelo y una huella SHA-256. Solo se agrega: nadie puede editar ni borrar una entrada.",
  "admin.aiLogEmpty": "Aún no hay resultados de IA registrados.",
  "admin.aiOperation": "Operación",
  "admin.aiModel": "Modelo",
  "admin.aiShow": "Ver resultado",
  "admin.aiHide": "Ocultar",
  "billing.setupLabel": "INSTALACIÓN ÚNICA",
  "billing.monthlyLabel": "SERVICIO MENSUAL + IA",
  "billing.perMonth": "/ mes",
  "billing.firstCharge": "Primer cargo hoy: {amount} (instalación + primer mes).",
  "billing.canceledThrough": "Cancelada. El acceso continúa hasta el {date}.",
  "billing.pastDue": "La última renovación falló. Actualice la tarjeta en Stripe antes del {date} para conservar el acceso.",
  "billing.includedLabel1": "SERVICIO",
  "billing.included1": "Incorporación, el análisis de la contienda y el manual, y acompañamiento directo de Elevate El Paso.",
  "billing.includedLabel2": "IA INCLUIDA",
  "billing.included2": "AURA, el vigilante, OCR y transcripción están incluidos en la mensualidad con un límite de uso razonable.",
  "billing.includedLabel3": "SEGURIDAD",
  "billing.included3": "Los pagos se hacen en Stripe; la aplicación nunca ve datos de tarjeta ni guarda una clave secreta de Stripe.",
  "nav.strategy": "Análisis y estrategia",
  "auth.emailOrUsername": "Correo o usuario",
  "auth.resetEmailOnly": "Las cuentas con usuario las restablece el administrador de la campaña.",
  "guide.startTour": "Iniciar el recorrido guiado",
  "pw.title": "Cree su propia contraseña",
  "pw.lede": "Bienvenido, {name}. Entró con una contraseña temporal. Elija una nueva para continuar.",
  "pw.new": "Contraseña nueva",
  "pw.confirm": "Confirme la contraseña nueva",
  "pw.submit": "Guardar y continuar",
  "pw.tooShort": "Use al menos {n} caracteres.",
  "pw.mismatch": "Las dos contraseñas no coinciden.",
  "pw.relogin": "Por seguridad, cierre sesión, vuelva a entrar con la contraseña temporal e inténtelo de nuevo.",
  "pw.failed": "No se pudo cambiar la contraseña. Inténtelo de nuevo.",
  "welcome.eyebrow": "BIENVENIDO",
  "welcome.title": "Bienvenido, {name}",
  "welcome.lede": "Preparamos dos documentos para usted. Empiece por el análisis de la contienda y luego lea su estrategia sugerida. Haga el recorrido por toda la aplicación ahora o después desde la Guía.",
  "welcome.ledeGeneric": "Haga un recorrido rápido por lo que hace la aplicación o explore por su cuenta. El recorrido siempre está disponible en la Guía.",
  "welcome.raceAnalysis": "Ver el análisis de la contienda",
  "welcome.raceAnalysisHint": "El distrito, los votantes, los temas y los cinco candidatos",
  "welcome.playbook": "Ver su estrategia sugerida",
  "welcome.playbookHint": "Mensaje, plataforma, oponentes, vulnerabilidades, dinero y calendario",
  "welcome.tour": "Hacer el recorrido",
  "welcome.explore": "Explorar por mi cuenta",
  "tour.step": "RECORRIDO · {n}/{total}",
  "tour.back": "Atrás",
  "tour.next": "Siguiente",
  "tour.finish": "Terminar",
  "tour.skip": "Saltar recorrido",
  "tour.close": "Cerrar",
  "strategy.title": "Análisis y estrategia",
  "strategy.lede": "El análisis de la contienda del Distrito 8 y el manual de su campaña, preparados por Elevate El Paso.",
  "strategy.raceAnalysis": "Análisis de la contienda",
  "strategy.playbook": "Manual de campaña",
  "strategy.contents": "Contenido",
  "strategy.print": "Imprimir",
  "strategy.missing": "Este documento todavía no se ha cargado.",
  "strategy.confidential": "Confidencial. Solo lo ven el candidato y la dirección de la campaña. Las proyecciones y valoraciones son opinión de los autores; los hechos tienen fuente en el análisis de la contienda.",
  "tour.dashboard.title": "Centro de mando",
  "tour.dashboard.body": "Todo de un vistazo: el reloj electoral, plazos, revisiones pendientes, actividad de campo y alertas de narrativa, en orden de prioridad.",
  "tour.strategy.title": "Análisis y estrategia",
  "tour.strategy.body": "Aquí están su análisis de la contienda y su manual de campaña, siempre a un clic. No comparta nada de ellos públicamente sin verificar las fuentes.",
  "tour.advisor.title": "Pregúntale a la campaña",
  "tour.advisor.body": "Pregunte lo que quiera sobre la contienda. AURA responde solo con evidencia aprobada, cita sus fuentes y separa los hechos de las recomendaciones.",
  "tour.memory.title": "Memoria institucional",
  "tour.memory.body": "Hechos aprobados, decisiones, doctrina del manual y notas de trabajo que usa el asesor. Nada entra sin revisión.",
  "tour.evidence.title": "Evidencia",
  "tour.evidence.body": "Cada documento, video y artículo en que se apoya la campaña: cargado, transcrito, revisado y calificado por confiabilidad antes de que la IA lo use.",
  "tour.claims.title": "Aprobar hechos",
  "tour.claims.body": "Apruebe o rechace los hechos extraídos de la evidencia. Solo las afirmaciones aprobadas respaldan las respuestas del asesor.",
  "tour.watchdog.title": "Vigilante",
  "tour.watchdog.body": "Monitorea la cobertura pública de cada candidato tres veces al día y muestra qué historias pasan de rumor a noticia.",
  "tour.warRoom.title": "Sala de guerra",
  "tour.warRoom.body": "El catálogo de herramientas controladas que la campaña puede usar y quién puede usar cada una.",
  "tour.field.title": "Campo",
  "tour.field.body": "Ubicación en vivo de los equipos (con consentimiento), turnos y trabajo puerta a puerta.",
  "tour.contacts.title": "Contactos",
  "tour.contacts.body": "El CRM de la campaña: simpatizantes, donantes, voluntarios y cada interacción.",
  "tour.projects.title": "Proyectos y trabajo",
  "tour.projects.body": "Metas, hitos, tareas, temas, decisiones y riesgos: el plan convertido en asignaciones.",
  "tour.calendar.title": "Calendario",
  "tour.calendar.body": "Sus eventos y los del equipo en una sola agenda.",
  "tour.board.title": "Tablero del equipo",
  "tour.board.body": "Mensajería de toda la campaña con @menciones.",
  "tour.report.title": "Informe de campaña",
  "tour.report.body": "Un informe listo para decidir: objetivos, plazos, riesgos, cumplimiento y avance.",
  "tour.hermes.title": "Hermes",
  "tour.hermes.body": "Asistente de redacción para trabajos supervisados; cada resultado se revisa antes de usarse.",
  "tour.finance.title": "Finanzas y cumplimiento",
  "tour.finance.body": "Gastos, proveedores, reportes y revisiones automáticas de cumplimiento, para que reportar nunca sea una carrera.",
  "tour.guide.title": "Guía",
  "tour.guide.body": "Instrucciones paso a paso para cada pantalla. Reinicie este recorrido desde aquí cuando quiera.",
  "nav.billing": "Licencia",
  "nav.activate": "Active su campaña",
  "hermes.title": "Mesa de redacción",
  "hermes.subtitle": "Motor supervisado para investigación, planeación y borradores. Todo resultado es una propuesta hasta que una persona lo apruebe.",
  "hermes.queue": "Cola de trabajo",
  "hermes.newRun": "Pedir un borrador",
  "hermes.instructions": "Contexto y restricciones",
  "hermes.instructionsPlaceholder": "Incluye la decisión, fecha límite y fuentes que debe usar…",
  "hermes.run": "Enviar a la mesa de redacción",
  "hermes.empty": "Todavía no se ha pedido ningún borrador.",
  "hermes.emptyBody":
    "Pide un informe matutino, una actualización de investigación o un borrador bilingüe. Todo resultado vuelve como propuesta para que una persona lo apruebe.",
  "hermes.offline": "Cómo se producen los borradores",
  "hermes.offlineText": "El trabajo en cola se procesa solo cuando el trabajador privado de redacción de la campaña está activo. CampaignOS nunca aprueba comandos del sistema ni publica resultados automáticamente.",
  "hermes.approve": "Aprobar propuesta",
  "hermes.reject": "Rechazar",
  "hermes.cancel": "Solicitar detener",
  "hermes.output": "Propuesta",
  "hermes.pending": "Revisión humana pendiente",
  "hermes.type.morning_brief": "Informe matutino",
  "hermes.type.research_refresh": "Actualizar investigación",
  "hermes.type.task_triage": "Priorizar tareas",
  "hermes.type.source_validation": "Validar fuentes",
  "hermes.type.meeting_prep": "Preparar reunión",
  "hermes.type.bilingual_draft": "Borrador bilingüe",
  "common.loading": "Cargando…",
  "common.error": "Algo salió mal",
  "common.retry": "Reintentar",
  "common.none": "Ninguno",
  "common.required": "Obligatorio",
  "common.optional": "opcional",
  "common.noPermission": "No tienes permiso para ver esto.",
  "common.back": "Volver",
  "search.open": "Buscar",
  "search.empty": "Sin resultados.",
  "search.kind.evidence": "Evidencia",
  "search.kind.memory": "Memoria",
  "search.kind.task": "Tarea",
  "search.kind.contact": "Contacto",
  "common.of": "de",

  "memory.title": "Lo que sabemos",
  "memory.subtitle":
    "Hechos revisados, eventos con fecha, nuestro manual y notas de esta semana. Los hechos siempre enlazan a una fuente, y el manual se mantiene aparte para que nunca se trate como hecho.",
  "memory.tier": "Tipo",
  "memory.tier.semantic": "Hechos",
  "memory.tier.episodic": "Cronología",
  "memory.tier.procedural": "Manual de campaña",
  "memory.tier.working": "Notas de esta semana",
  "memory.tier.semantic.help": "Hechos firmes y revisados. Debe enlazar a una fuente.",
  "memory.tier.episodic.help": "Eventos con fecha en la historia de la campaña. Debe enlazar a una fuente.",
  "memory.tier.procedural.help": "Cómo hace las cosas esta campaña. Se usa como consejo, nunca como hecho.",
  "memory.tier.working.help": "En qué estamos enfocados ahora mismo. Solo contexto.",
  "memory.add": "Agregar lo que sabemos",
  "memory.statement": "Enunciado",
  "memory.rationale": "Fundamento (por qué funciona)",
  "memory.evidence": "IDs de evidencia",
  "memory.evidenceHelp": "IDs de fuente, separados por comas. Obligatorio para hechos y cronología.",
  "memory.occurredAt": "Ocurrió el",
  "memory.entities": "Personas / entidades",
  "memory.clusters": "Temas",
  "memory.confidence": "Confianza",
  "memory.create": "Guardar",
  "memory.status": "Estado",
  "memory.status.proposed": "Propuesta",
  "memory.status.approved": "Aprobada",
  "memory.status.retired": "Retirada",
  "memory.approve": "Aprobar",
  "memory.retire": "Retirar",
  "memory.empty": "Todavía no hay nada aquí.",
  "memory.emptyBody":
    "Aquí la campaña guarda lo que ya verificó, para que el asesor y el informe diario puedan apoyarse en ello.",
  "memory.proposedNote": "Registrada como propuesta. Llega al asesor una vez aprobada.",
  "memory.doctrineNote":
    "La doctrina se puede usar de inmediato, pero el asesor la presenta como recomendación, nunca como hecho.",
  "memory.working.title": "Notas de esta semana",
  "memory.working.objectives": "Objetivos (uno por línea)",
  "memory.working.notes": "Notas",
  "memory.working.save": "Guardar las notas de la semana",
  "memory.working.saved": "Guardado. El asesor lo tomará como el enfoque actual.",

  "war.title": "Herramientas de investigación",
  "war.subtitle":
    "El conjunto de herramientas de inteligencia y análisis que una campaña puede desplegar. Cada una se describe por lo que hace y cómo usarla de forma defendible — porque en una campaña, cómo las usas importa tanto como lo que encuentran.",
  "war.category.records": "Inteligencia de registros públicos",
  "war.category.discourse": "Monitoreo del discurso público",
  "war.category.simulation": "Simulación de estrategia",
  "war.category.osint": "Herramientas OSINT y de huella digital",
  "war.category.knowledge": "Investigación y base de conocimiento",
  "war.what": "Qué hace",
  "war.use": "Uso en campaña",
  "war.guardrail": "Nota de uso defendible",
  "war.status.live": "Activo",
  "war.status.recommended": "Recomendado",
  "war.status.operatorRun": "Operado por especialista",
  "war.status.roadmap": "En hoja de ruta",
  "war.status.caution": "Manejar con asesoría legal",
  "war.recordsIntro":
    "El núcleo defendible. Todo aquí es registro público — informes, votos, membresías de juntas, periodismo publicado — estructurado en la misma base de evidencia citada que usa el asesor. Esto es lo que gana los argumentos y resiste el escrutinio.",
  "war.osintIntro":
    "Potentes, de doble uso, y las herramientas con mayor riesgo si se apuntan a individuos privados. Una campaña debe usarlas solo contra sí misma (auditoría de exposición) o con asesoría legal, nunca contra periodistas o personas privadas. Esa gobernanza es el diferenciador, no las herramientas.",

  "war.tool.foxel.name": "Foxel",
  "war.tool.foxel.what": "Biblioteca multimedia autoalojada con etiquetado y búsqueda por IA sobre imágenes, video y documentos.",
  "war.tool.foxel.use": "Un archivo privado y buscable de cada foto, clip e informe escaneado que la campaña recoge en territorio — sin enviar nada a terceros.",
  "war.tool.surfsense.name": "SurfSense",
  "war.tool.surfsense.what": "Asistente de investigación privado que responde preguntas sobre tus documentos, con citas (un NotebookLM autoalojado).",
  "war.tool.surfsense.use": "Leer a fondo un corpus de informes sin exponerlo a un servicio de IA público. Alimenta extractos candidatos a la base de evidencia.",
  "war.tool.nocodb.name": "NocoDB",
  "war.tool.nocodb.what": "Airtable de código abierto: una base de datos tipo hoja de cálculo con interfaz compartible.",
  "war.tool.nocodb.use": "Registrar donantes, voluntarios, territorio y declaraciones de proveedores en tablas que el personal no técnico puede editar.",
  "war.tool.spiderfoot.name": "SpiderFoot",
  "war.tool.spiderfoot.what": "Marco de automatización que recopila inteligencia de fuentes abiertas sobre un dominio, organización o huella digital desde más de 200 fuentes.",
  "war.tool.spiderfoot.use": "Auditar la propia exposición de la campaña: qué es públicamente descubrible sobre el candidato, el equipo y la infraestructura — antes de que lo encuentre un rival.",
  "war.tool.reconng.name": "recon-ng",
  "war.tool.reconng.what": "Marco modular de reconocimiento para recopilación de datos de fuentes abiertas, con forma de consola de pentest.",
  "war.tool.reconng.use": "Autoauditoría estructurada de la superficie en línea de la campaña. Operado por especialista bajo un alcance definido.",
  "war.tool.shodan.name": "Shodan",
  "war.tool.shodan.what": "Motor de búsqueda de dispositivos y servicios conectados a internet.",
  "war.tool.shodan.use": "Revisar los propios servidores, cámaras y servicios de la campaña por exposición pública accidental.",
  "war.tool.sherlock.name": "Sherlock",
  "war.tool.sherlock.what": "Encuentra un nombre de usuario en cientos de plataformas sociales.",
  "war.tool.sherlock.use": "Defensa de marca y suplantación: encontrar cuentas que ocupan el nombre del candidato o suplantan a la campaña.",
  "war.tool.ghunt.name": "GHunt",
  "war.tool.ghunt.what": "Investiga qué expone públicamente una cuenta de Google.",
  "war.tool.ghunt.use": "Revisar qué filtran las propias cuentas de Google de la campaña. No para usar contra individuos privados.",
  "war.tool.spiderfoot.guard": "Ejecutar contra tus propios activos bajo un alcance escrito. Contra terceros, solo hechos de registro público entran a la evidencia.",
  "war.tool.people.guard": "Enfocadas en personas. Usar solo para la auditoría de exposición de la propia campaña o defensa de marca, con asesoría legal — nunca contra periodistas o ciudadanos privados.",
  "war.tool.betta.name": "Monitor de Discurso (inspirado en BettaFish)",
  "war.tool.betta.what": "Monitoreo multiagente del discurso político público, con análisis de sentimiento y un 'foro' de agentes que debate qué significa la señal.",
  "war.tool.betta.use": "Seguir cómo se mueven los temas del distrito en la conversación pública y los medios locales — etiquetado como anecdótico, nunca como encuesta. Adaptado a fuentes de EE. UU./locales; el original apunta a plataformas chinas.",
  "war.tool.betta.guard": "Solo publicaciones públicas, reportadas como señal, no como medición. Sin grupos privados, sin perfilado encubierto.",
  "war.tool.miro.name": "Simulador de Mensaje (inspirado en MiroFish)",
  "war.tool.miro.what": "Simulación basada en agentes: modelar cómo podrían reaccionar los segmentos de votantes del distrito a un mensaje antes de enviarlo — un grupo focal sintético.",
  "war.tool.miro.use": "Poner a prueba un reencuadre o una línea de ataque contra segmentos modelados, luego probar el ganador con personas reales. Nunca sustituye a los votantes reales.",
  "war.tool.miro.guard": "Una herramienta de ensayo. Los resultados son hipótesis a probar, no hallazgos — y nunca segmentación individualizada.",
  "war.tool.records.name": "Mesa de Registros Públicos",
  "war.tool.records.what": "Informes de financiamiento de campaña, votos nominales, membresías de juntas y periodismo publicado, estructurados en la base de evidencia citada.",
  "war.tool.records.use": "La columna vertebral de la investigación de oposición y propia. 'Sigue el dinero, sigue el poder' desde documentos que cualquiera puede obtener — que es justo por lo que resiste el escrutinio.",

  "watch.title": "Lo que se está diciendo",
  "watch.subtitle":
    "Tres veces al día esto revisa la web pública en busca de menciones de la contienda — el campo del Distrito 8, los otros distritos en esta boleta y la alcaldía — para que la campaña se entere a tiempo. Muestra qué se dice y hacia dónde se mueve la atención. Nunca es una encuesta.",
  "watch.anecdotalBanner":
    "Todo aquí es conversación y cobertura pública anecdótica, no medición. El asesor lo trata como señal, nunca como hecho, y nunca lo cita.",
  "watch.runNow": "Buscar menciones ahora",
  "watch.running": "Rastreando la web…",
  "watch.lastRun": "Último rastreo",
  "watch.never": "nunca",
  "watch.total": "Menciones rastreadas",
  "watch.last7d": "Últimos 7 días",
  "watch.last24h": "Últimas 24 horas",
  "watch.byEntity": "Por figura",
  "watch.bySentiment": "Sentimiento",
  "watch.bySource": "Por tipo de fuente",
  "watch.recent": "Menciones recientes",
  "watch.empty": "Todavía no se ha recogido nada.",
  "watch.emptyBody":
    "Esta página reúne lo que se dice en público sobre la contienda — noticias, blogs y redes. Busca ahora o espera la siguiente búsqueda automática.",
  "watch.sentiment.positive": "Positivo",
  "watch.sentiment.neutral": "Neutral",
  "watch.sentiment.negative": "Negativo",
  "watch.runResult": "Rastreo completo: {fresh} menciones nuevas ({found} encontradas).",
  "watch.schedule": "Los rastreos automáticos corren a las 8am, 1pm y 7pm, hora de El Paso.",
  "watch.lastRunFailed": "El último rastreo falló — revisa la clave de la API",

  "watch.meter": "Hasta dónde ha llegado una historia",
  "watch.meterHelp":
    "La altitud de cada narrativa en la escala de fiabilidad, rastreada en el tiempo. Un tema que sube de rumor de blog a noticia es un salto — el momento de responder.",
  "watch.meterEmpty": "Aún no hay narrativas rastreadas. Se forman a medida que se acumulan menciones entre rastreos.",
  "watch.rung.social": "Redes",
  "watch.rung.blog": "Blog",
  "watch.rung.local_news": "Noticia local",
  "watch.rung.established": "Consolidada",
  "watch.rung.official_record": "Registro oficial",
  "watch.momentum.rising": "Subiendo",
  "watch.momentum.steady": "Estable",
  "watch.momentum.falling": "Bajando",
  "watch.breakout": "SALTO a noticia",
  "watch.promotable": "En registro oficial — apta para promover a evidencia",
  "watch.conduct": "Menciones que requieren tu atención",
  "watch.conductHelp":
    "Respuestas desde tu propia cuenta a quienes te critican. Regla permanente: tú no discutes en comentarios — las respuestas salen por personal nombrado, para que nada de esto se vuelva la noticia.",
  "watch.conductNone": "Nada marcado. Sigue canalizando las respuestas por tu equipo.",
  "watch.acknowledge": "Reconocer",
  "dash.conductAlert": "Menciones que requieren tu atención",
  "dash.conductAlertBody":
    "Hay {n} mención(es) sobre ti sin revisar. Habla con la gerencia de campaña antes de dar declaraciones.",

  "field.title": "Mapa de campo",
  "field.subtitle":
    "Ubicaciones en vivo del equipo que está compartiendo, para que la campaña movilice y dirija al candidato. Solo en vivo — no se guarda historial de ubicación, y cada persona controla su propio uso.",
  "field.share": "Compartir mi ubicación",
  "field.stop": "Dejar de compartir / terminar turno",
  "field.sharingOn": "Estás compartiendo tu ubicación en vivo con los coordinadores. Se detiene en cuanto termines el turno o cierres esta pestaña, y no se guarda historial.",
  "field.consentTitle": "Antes de compartir",
  "field.consentBody":
    "Compartir muestra a los coordinadores dónde estás mientras trabajas las calles, para coordinar y enviarte al candidato. Es solo en vivo — nada se registra después de que te detengas. Puedes detenerte cuando quieras.",
  "field.shiftLabel": "Etiqueta de turno / territorio (opcional)",
  "field.asCandidate": "Compartir como el candidato",
  "field.denied": "Se denegó el permiso de ubicación. Actívalo en tu navegador para compartir.",
  "field.unavailable": "Tu dispositivo o navegador no puede dar ubicación.",
  "field.acquiring": "Obteniendo tu ubicación…",
  "field.crewOnline": "Equipo compartiendo ahora",
  "field.noneOnline": "Nadie está compartiendo su ubicación en este momento.",
  "field.noneOnlineBody":
    "Quienes activan el compartir aparecen aquí mientras dura su turno. Después no se guarda nada.",
  "field.lastUpdate": "Actualizado",
  "field.stale": "Sin señal",
  "field.you": "Tú",
  "field.candidate": "Candidato",
  "field.directions": "Cómo llegar",
  "field.recenter": "Recentrar",
  "field.accuracy": "±{m} m",
  "field.mapUnavailable": "El mapa no se pudo cargar.",
  "field.managerHint": "Toca un marcador para ver quién es, cuándo se actualizó y cómo llegar.",
  "field.crewHint": "Solo los coordinadores ven el mapa. Tú solo controlas tu propio uso compartido.",

  "cal.title": "Calendario",
  "cal.subtitle": "Todo lo que el candidato y el equipo tienen agendado.",
  "cal.add": "Nuevo evento",
  "cal.eventTitle": "Evento",
  "cal.start": "Inicia",
  "cal.end": "Termina",
  "cal.location": "Lugar",
  "cal.type": "Tipo",
  "cal.type.canvass": "Recorrido",
  "cal.type.fundraiser": "Recaudación",
  "cal.type.media": "Medios / entrevista",
  "cal.type.debate": "Debate / foro",
  "cal.type.community": "Evento comunitario",
  "cal.type.meeting": "Reunión",
  "cal.type.deadline": "Fecha límite",
  "cal.audience": "Para",
  "cal.audience.all": "Toda la campaña",
  "cal.audience.candidate": "Candidato",
  "cal.audience.crew": "Equipo de territorio",
  "cal.notes": "Notas",
  "cal.create": "Agregar al calendario",
  "cal.empty": "No hay eventos programados.",
  "cal.emptyBody":
    "Recorridos, foros, recaudaciones y prensa — todo a lo que el candidato y el equipo tienen que llegar.",
  "cal.today": "Hoy",
  "cal.tomorrow": "Mañana",
  "cal.upcoming": "Próximos",
  "cal.filterAll": "Todos",
  "cal.at": "a las",

  "board.title": "Chat del equipo",
  "board.subtitle": "Chat de campaña. Etiqueta a tus compañeros y adjunta evidencia, eventos o herramientas a cualquier mensaje.",
  "board.channel.general": "general",
  "board.channel.field": "territorio",
  "board.channel.comms": "comunicación",
  "board.channel.strategy": "estrategia",
  "board.compose": "Escribe un mensaje… usa @ para mencionar, o adjunta abajo",
  "board.send": "Enviar",
  "board.mention": "@ Mencionar",
  "board.attach": "# Adjuntar",
  "board.mentionsMe": "Me mencionan",
  "board.all": "Todos",
  "board.empty": "Aún no hay mensajes.",
  "board.emptyBody": "Aquí habla el equipo. Elige un canal y di lo primero.",
  "board.attachEvidence": "Evidencia",
  "board.attachEvent": "Evento",
  "board.attachTool": "Herramienta",
  "board.pickUser": "Elige un compañero",
  "board.pickEvidence": "Elige evidencia",
  "board.pickEvent": "Elige un evento",
  "board.pickTool": "Elige una herramienta",
  "board.mentioning": "Mencionando",
  "board.attached": "Adjunto",

  "proj.title": "El plan",
  "proj.subtitle":
    "Los objetivos y resultados clave guían la estrategia; los hitos marcan el calendario; el tablero de ideas captura la investigación antes de que se vuelva trabajo. Lo mejor de una herramienta de estrategia y una de proyectos, en un solo lugar.",
  "proj.goals": "Objetivos y resultados clave",
  "proj.goalsHelp": "Qué queremos lograr y cómo lo mediremos.",
  "proj.addGoal": "Nuevo objetivo",
  "proj.objective": "Objetivo",
  "proj.why": "Por qué importa",
  "proj.keyResults": "Resultados clave (medibles)",
  "proj.krLabel": "Resultado",
  "proj.krTarget": "Meta",
  "proj.krCurrent": "Actual",
  "proj.addKr": "Agregar resultado clave",
  "proj.goalProgress": "Progreso",
  "proj.saveGoal": "Guardar objetivo",
  "proj.noGoals": "Aún no hay objetivos.",
  "proj.noGoalsBody": "Di cómo se ve ganar, y cómo sabrá la campaña que llegó.",
  "proj.goal.active": "Activo",
  "proj.goal.done": "Logrado",
  "proj.goal.paused": "En pausa",
  "proj.milestones": "Hitos",
  "proj.milestonesHelp": "Puntos de control con fecha rumbo al día de la elección.",
  "proj.addMilestone": "Nuevo hito",
  "proj.msTitle": "Hito",
  "proj.due": "Vence",
  "proj.noMilestones": "No hay hitos definidos.",
  "proj.noMilestonesBody": "Puntos de control con fecha rumbo al día de la elección, para ver el retraso a tiempo.",
  "proj.ms.pending": "Pendiente",
  "proj.ms.done": "Hecho",
  "proj.ms.missed": "Vencido",
  "proj.linkGoal": "Objetivo vinculado",
  "proj.none": "Ninguno",
  "proj.ideas": "Tablero de ideas e investigación",
  "proj.ideasHelp": "Captura una idea de estrategia antes de que sea tarea. Muévela a la derecha conforme se concreta.",
  "proj.addIdea": "Nueva idea",
  "proj.promoteToGoal": "Convertir en objetivo →",
  "proj.ideaTitle": "Idea",
  "proj.ideaDetail": "Detalle",
  "proj.tags": "Etiquetas",
  "proj.idea.new": "Nueva",
  "proj.idea.researching": "Investigando",
  "proj.idea.validated": "Validada",
  "proj.idea.parked": "En pausa",
  "proj.noIdeas": "Nada en esta etapa.",
  "proj.moveTo": "Mover",
  "proj.markDone": "Marcar logrado",

  "set.title": "Ajustes",
  "set.subtitle": "Apariencia, tu cuenta, la guía de usuario y el recorrido inicial.",
  "set.theme": "Tema de color",
  "set.theme.system": "Sistema",
  "set.theme.light": "Claro",
  "set.theme.dark": "Oscuro",
  "set.language": "Idioma",
  "set.account": "Tu cuenta",
  "set.role": "Roles",
  "set.permissions": "Permisos efectivos",
  "set.guide": "Guía de usuario",
  "set.guideBody": "Un recorrido por cada función y flujo de trabajo.",
  "set.guideOpen": "Abrir la guía",
  "set.tour": "Recorrido inicial",
  "set.tourBody": "Un breve recorrido guiado por la app.",
  "set.tourStart": "Iniciar el recorrido",
  "set.notify.title": "Notificaciones",
  "set.notify.body":
    "Los correos llegan a {email}. No se envía nada hasta que lo actives. Horas de silencio: {start}–{end}, hora de El Paso.",
  "set.notify.noEmail": "el correo de tu cuenta",
  "set.notify.briefing": "Resumen matutino por correo (7:00 a. m.)",
  "set.notify.briefingHint": "Días para la elección, dinero y simpatizantes, tus tareas, el próximo evento.",
  "set.notify.media": "Alertas de oportunidades de medios urgentes",
  "set.notify.mediaHint": "Solo para oportunidades marcadas Ahora. En horas de silencio esperan al resumen de las 7:00.",
  "set.notify.saving": "Guardando…",
  "set.notify.saved": "Guardado.",
  "set.notify.saveFailed": "No se pudo guardar: {error}",
  "set.notify.test": "Enviarme un correo de prueba",
  "set.notify.testing": "Enviando…",
  "set.notify.testSent": "Correo de prueba enviado a {email}.",
  "set.notify.testFailed": "No se envió el correo de prueba: {error}",

  "asst.title": "Mejor siguiente paso",
  "asst.subtitle": "Sugerencias a partir de los datos en vivo de tu campaña.",
  "asst.none": "Todo bajo control — nada urgente ahora mismo.",
  "asst.go": "Ir",
  "asst.s.conduct": "Hay {n} mención(es) sobre ti sin revisar. Habla con la gerencia de campaña antes de dar declaraciones.",
  "asst.s.breakout": "Una historia saltó a cobertura de noticias. Informa al equipo.",
  "asst.s.election": "Falta(n) {n} día(s) para la elección.",
  "asst.s.runoff": "Falta(n) {n} día(s) para la segunda vuelta.",
  "asst.s.noEvidence": "El asesor todavía no tiene nada que citar. Aprueba una fuente para que sirva.",
  "asst.s.pending": "{n} documento(s) esperan revisión. Apruébalos para que el asesor pueda citarlos.",
  "asst.s.flags": "{n} alerta(s) de cumplimiento abierta(s).",
  "asst.s.objectives": "Define los objetivos de esta semana para enfocar al asesor.",
  "asst.s.goals": "Define un objetivo de campaña con resultados clave.",
  "asst.s.overdue": "{n} tarea(s) vencida(s).",
  "asst.dismiss": "Descartar",
  "asst.urgent": "{n} requiere(n) atención ahora",

  "wiz.skip": "Omitir",
  "wiz.next": "Siguiente",
  "wiz.back": "Atrás",
  "wiz.done": "Terminar",
  "wiz.step": "Paso {n} de {total}",
} satisfies Record<keyof typeof baseEn, string>;

const es = {
  ...baseEs,
  ...briefingEs,
  ...callTimeEs,
  ...practiceEs,
  ...mediaScoutEs,
} satisfies Record<StringKey, string>;

/** The shared dictionaries without feature modules, for the collision test. */
export const baseStrings = { en: baseEn, es: baseEs };

const DICTIONARIES: Record<Locale, Record<StringKey, string>> = { en, es };

export function translate(
  locale: Locale,
  key: StringKey,
  vars?: Record<string, string | number>,
): string {
  const template = DICTIONARIES[locale][key] ?? en[key] ?? key;
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

/** Locale-correct dates and money, so "1.234,56" never shows up mislabelled. */
export function formatDate(locale: Locale, value: Date | null | undefined): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(locale === "es" ? "es-MX" : "en-US", {
    dateStyle: "medium",
  }).format(value);
}

export function formatDateTime(
  locale: Locale,
  value: Date | null | undefined,
): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(locale === "es" ? "es-MX" : "en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(value);
}

/**
 * Parses a `<input type="date">` value ("YYYY-MM-DD") as LOCAL midnight.
 *
 * `new Date("2026-08-15")` parses a date-only ISO string as UTC midnight per
 * spec. Displayed in any timezone west of UTC (El Paso is UTC-6/-7), that
 * lands on the previous day — a due date typed as the 15th shows as the 14th.
 * Building the Date from numeric parts uses the local timezone instead.
 */
export function parseDateInputLocal(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, m, d] = match;
  return new Date(Number(y), Number(m) - 1, Number(d));
}

/**
 * The calendar day a Date falls on, in the viewer's local timezone.
 *
 * `date.toISOString().slice(0, 10)` is UTC, so an evening event west of UTC
 * (e.g. 8pm Mountain Time) serializes to after-midnight UTC the NEXT day and
 * groups under the wrong date.
 */
export function localDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/**
 * Issue clusters are stored as their canonical English string (the data
 * contract every writer and every Firestore query uses, e.g. issueClusters:
 * ["Government finance"]) — that never changes. This only maps the stored
 * value to its display key, so the label can render in the viewer's language
 * without touching how the value is stored, filtered, or queried.
 */
export function clusterKey(cluster: string): StringKey {
  return `cluster.${cluster.toLowerCase().replace(/\s+/g, "_")}` as StringKey;
}

export function formatMoney(locale: Locale, cents: number): string {
  return new Intl.NumberFormat(locale === "es" ? "es-MX" : "en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}
